import { EditorApp, type ParamInfo, type ScadError, type TransformInfo, type ScadBounds } from './EditorApp';

// Stale-bundle recovery: after a fresh deploy, a tab that was open before
// still has the OLD index-*.js cached. When it tries to dynamically import
// a chunk (e.g. STLLoader, sketch editor) the server returns 404 because
// the chunk got a new hash. Vite emits `vite:preloadError` for exactly
// this case — reload the page so the user picks up the fresh bundle and
// the operation can proceed. Runs at the earliest possible point so any
// pending deferred import is caught.
window.addEventListener('vite:preloadError', (event) => {
  (event as Event).preventDefault();
  console.warn('[scad+] Stale bundle detected — reloading to fetch updated assets.');
  window.location.reload();
});

import { decodeScadFromHash, isPreviewHash, isEmbedHash, buildShareUrl, buildPreviewUrl, buildEmbedUrl } from './shareUrl';
import { getCurrentTheme, type ThemeMode } from './theme';
import { AIChatPanel, createSettingsModal } from './aiChat';
import { parseCustomizerFields, type CustomizerField } from './customizer';
import { buildOutline, type RenderNode, type OutlineData } from './outline';
import type { MeasureMode } from '../viewport/MeasureTool';
import type { SectionAxis, SectionState } from './section';
import { snapDelta, type SnapAnchor, type AxisAnchor, type SnapTarget, type Box3, type V3 } from './snap';

// --- Preview / Embed mode detection ---

const isEmbed = isEmbedHash(window.location.hash);
const isPreview = isEmbed || isPreviewHash(window.location.hash);
if (isEmbed) document.body.classList.add('embed');
if (isPreview) document.body.classList.add('preview');

// --- DOM references ---

const editorContainer = document.getElementById('editor-container')!;
const viewportContainer = document.getElementById('viewport-container')!;
const statVertices = document.getElementById('stat-vertices')!;
const statFaces = document.getElementById('stat-faces')!;
const statTime = document.getElementById('stat-time')!;
const statBounds = document.getElementById('stat-bounds')!;
const statusRendering = document.getElementById('status-rendering')!;
const vpLive = document.getElementById('vp-live')!;
const vpFnLabel = document.getElementById('vp-fn')!;
const vpProjection = document.getElementById('vp-projection')!;
const vpViewCubeLabel = document.getElementById('vp-view-cube-label')!;
const tbFilename = document.getElementById('tb-filename') as HTMLElement | null;
const tbModifiedDot = document.getElementById('tb-modified') as HTMLElement | null;
const consoleStripMsg = document.getElementById('console-strip-msg')!;
const consoleStripEl = document.getElementById('console-strip')!;

// --- Create app ---

const app = new EditorApp(editorContainer, viewportContainer, isPreview ? { viewOnly: true } : undefined);
(window as any).__app = app;

// --- Stats updates (both modes) ---

app.onStatsUpdate((vertices, faces, timeMs) => {
  statVertices.textContent = vertices.toLocaleString();
  statFaces.textContent = faces.toLocaleString();
  statTime.textContent = timeMs.toFixed(1);
});

// --- Rendering indicator (both modes) ---

const viewportSpinner = document.getElementById('viewport-spinner-overlay')!;
// hide spinner on startup until first render kicks off
viewportSpinner.style.display = 'none';

let pendingAutoFit = false;
app.onRenderingUpdate((rendering) => {
  statusRendering.style.display = rendering ? 'inline-flex' : 'none';
  viewportSpinner.style.display = rendering ? 'flex' : 'none';
  vpLive.classList.toggle('idle', !rendering);
  vpLive.textContent = rendering ? 'Rendering' : 'Idle';
  // When a tab switch requested an auto-fit, run it once rendering finishes
  if (!rendering && pendingAutoFit) {
    pendingAutoFit = false;
    app.resetCamera();
    // Fire a custom event so the edit-mode block (where renderOutline/
    // renderCustomizer are defined) can resync with the new tab's AST.
    window.dispatchEvent(new CustomEvent('scadplus:pipeline-complete'));
  }
});

// --- View controls (both modes) ---

const viewport = app.getViewport();
const projectionToggleBtn = document.getElementById('projection-toggle') as HTMLButtonElement | null;
const resetCameraBtn = document.getElementById('reset-camera') as HTMLButtonElement | null;
const fitSelectionBtn = document.getElementById('fit-selection') as HTMLButtonElement | null;

function syncProjectionUI(isOrtho: boolean): void {
  if (projectionToggleBtn) {
    projectionToggleBtn.textContent = isOrtho ? 'Persp' : 'Ortho';
    projectionToggleBtn.classList.toggle('active', isOrtho);
  }
  vpProjection.textContent = isOrtho ? 'Ortho' : 'Perspective';
  vpViewCubeLabel.textContent = isOrtho ? 'ORTHO' : 'PERSP';
}

if (projectionToggleBtn) {
  projectionToggleBtn.addEventListener('click', () => {
    const isOrtho = viewport.toggleProjection();
    syncProjectionUI(isOrtho);
  });
}
if (fitSelectionBtn) fitSelectionBtn.addEventListener('click', () => viewport.fitToSelection());
if (resetCameraBtn) resetCameraBtn.addEventListener('click', () => app.resetCamera());

// --- Theme (follows color profile; no manual toggle) ---

const themeColorMeta = document.querySelector('meta[name="theme-color"]') as HTMLMetaElement | null;

function updateThemeUI(mode: ThemeMode): void {
  if (themeColorMeta) {
    themeColorMeta.content = mode === 'dark' ? '#0a0b0d' : '#f5f3ee';
  }
}

updateThemeUI(getCurrentTheme());

// --- Color profiles ---

interface Profile {
  id: string;
  name: string;
  num: string;
  desc: string;
  mode: ThemeMode;
  preview: { bg: string; acc: string; k: string; n: string; s: string };
}

const PROFILES: Profile[] = [
  { id: 'blueprint', name: 'Blueprint',  num: '01',
    desc: 'Amber on graphite — the default engineering palette.', mode: 'dark',
    preview: { bg: '#15181c', acc: '#e9b155', k: '#c792ea', n: '#82b1ff', s: '#a5d6a7' } },
  { id: 'mono',      name: 'Monochrome', num: '02',
    desc: 'Pure grayscale. Zero hue distraction; shapes speak.', mode: 'dark',
    preview: { bg: '#17171a', acc: '#ffffff', k: '#ffffff', n: '#a1a1aa', s: '#e4e4e7' } },
  { id: 'mint',      name: 'Mint',       num: '03',
    desc: 'Cool teal accent on deep forest. Easy on long sessions.', mode: 'dark',
    preview: { bg: '#11211d', acc: '#6ed8b4', k: '#ff9e9e', n: '#f4c06a', s: '#b8d98f' } },
  { id: 'synth',     name: 'Synthwave',  num: '04',
    desc: 'Magenta + cyan on violet. Late-night rendering energy.', mode: 'dark',
    preview: { bg: '#1c1538', acc: '#ff5ec4', k: '#ff7edb', n: '#fede5d', s: '#72f1b8' } },
  { id: 'parchment', name: 'Parchment',  num: '05',
    desc: 'Warm paper with sepia accents — daylight mode.', mode: 'light',
    preview: { bg: '#ecebe4', acc: '#b8752b', k: '#8a4fb8', n: '#2762c9', s: '#3d7a3d' } },
  { id: 'solar',     name: 'Solar',      num: '06',
    desc: 'Solarized light — calibrated cream, high contrast.', mode: 'light',
    preview: { bg: '#f4ecd0', acc: '#cb4b16', k: '#6c71c4', n: '#268bd2', s: '#859900' } },
];

const PROFILE_KEY = 'scadplus-profile';
const profBtn     = document.getElementById('profBtn');
const profLabel   = document.getElementById('profLabel');
const profPop     = document.getElementById('profPop');
const profPopClose = document.getElementById('profPopClose');
const profList    = document.getElementById('profList');

function renderProfileList(activeId: string): void {
  if (!profList) return;
  profList.innerHTML = PROFILES.map(p => {
    const pv = p.preview;
    return `
      <div class="prof-row${p.id === activeId ? ' active' : ''}" data-prof="${p.id}">
        <div class="prof-meta">
          <div class="name"><span class="num">[${p.num}]</span> ${p.name}</div>
          <div class="desc">${p.desc}</div>
        </div>
        <div class="prof-preview" title="${p.name}">
          <div class="swatch" style="background:${pv.bg}"><span class="dot" style="background:${pv.acc}"></span></div>
          <div class="swatch" style="background:${pv.bg}"><span class="dot" style="background:${pv.k}"></span></div>
          <div class="swatch" style="background:${pv.bg}"><span class="dot" style="background:${pv.n}"></span></div>
          <div class="swatch" style="background:${pv.bg}"><span class="dot" style="background:${pv.s}"></span></div>
        </div>
      </div>`;
  }).join('');
  profList.querySelectorAll<HTMLElement>('.prof-row').forEach(row => {
    row.addEventListener('click', () => applyProfile(row.dataset.prof || 'blueprint', true));
  });
}

function applyProfile(id: string, persist: boolean): void {
  const p = PROFILES.find(x => x.id === id) || PROFILES[0];
  const root = document.documentElement;
  if (p.id === 'blueprint') {
    root.removeAttribute('data-profile');
  } else {
    root.setAttribute('data-profile', p.id);
  }
  // Force the light/dark mode that matches this profile so viewport grid + CM match
  root.setAttribute('data-theme', p.mode);
  try { localStorage.setItem('scadplus-theme', p.mode); } catch { /* ignore */ }
  app.setTheme(p.mode);
  updateThemeUI(p.mode);
  if (profLabel) profLabel.textContent = p.name;
  renderProfileList(p.id);
  if (persist) {
    try { localStorage.setItem(PROFILE_KEY, p.id); } catch { /* ignore */ }
    if (profPop) profPop.setAttribute('hidden', '');
  }
}

if (profBtn && profPop) {
  profBtn.addEventListener('click', (e) => {
    e.stopPropagation();
    const willOpen = profPop.hasAttribute('hidden');
    if (willOpen) profPop.removeAttribute('hidden');
    else profPop.setAttribute('hidden', '');
  });
  if (profPopClose) profPopClose.addEventListener('click', () => profPop.setAttribute('hidden', ''));
  document.addEventListener('click', (e) => {
    if (!profPop.hasAttribute('hidden') && !profPop.contains(e.target as Node) && e.target !== profBtn) {
      profPop.setAttribute('hidden', '');
    }
  });
  document.addEventListener('keydown', (e) => {
    if (e.key === 'Escape' && !profPop.hasAttribute('hidden')) profPop.setAttribute('hidden', '');
  });
}

// Apply saved (or blueprint) on load
let savedProfile = 'blueprint';
try { savedProfile = localStorage.getItem(PROFILE_KEY) || 'blueprint'; } catch { /* ignore */ }
applyProfile(savedProfile, false);

// --- Toast helper (both modes) ---

function showToast(message: string): void {
  const existing = document.getElementById('toast-notification');
  if (existing) existing.remove();
  const toast = document.createElement('div');
  toast.id = 'toast-notification';
  toast.className = 'toast-notification';
  toast.textContent = message;
  document.body.appendChild(toast);
  setTimeout(() => toast.remove(), 2600);
}

// --- Preview mode: load code and minimal keyboard ---

if (isPreview) {
  document.title = 'SCAD+ Preview';
  const sharedCode = decodeScadFromHash(window.location.hash);
  if (sharedCode) {
    app.loadCode(sharedCode);
  }

  window.addEventListener('keydown', (e) => {
    const active = document.activeElement;
    if (active && (active as HTMLElement).isContentEditable) return;

    if (e.code === 'Numpad1') { e.preventDefault(); viewport.setStandardView(e.ctrlKey ? 'back' : 'front'); return; }
    if (e.code === 'Numpad3') { e.preventDefault(); viewport.setStandardView(e.ctrlKey ? 'left' : 'right'); return; }
    if (e.code === 'Numpad7') { e.preventDefault(); viewport.setStandardView(e.ctrlKey ? 'bottom' : 'top'); return; }
    if (e.code === 'Numpad5') { e.preventDefault(); syncProjectionUI(viewport.toggleProjection()); return; }
    if (e.key.toLowerCase() === 'f') viewport.fitToSelection();
  });
}

// --- Edit mode: full UI setup ---

if (!isPreview) {

// DOM refs only needed in edit mode
const wireframeBtn = document.getElementById('wireframe-toggle') as HTMLButtonElement;
const solidBtn = document.getElementById('solid-toggle') as HTMLButtonElement | null;
const transformModeEl = document.getElementById('transform-mode')!;
const paramPanel = document.getElementById('param-panel')!;
const paramPanelTitle = document.getElementById('param-panel-title')!;
const paramPanelFields = document.getElementById('param-panel-fields')!;
const paramPanelClose = document.getElementById('param-panel-close')!;
const selStatus = document.getElementById('selection-status')!;
const selPosX = document.getElementById('sel-pos-x')!;
const selPosY = document.getElementById('sel-pos-y')!;
const selPosZ = document.getElementById('sel-pos-z')!;
const selRotX = document.getElementById('sel-rot-x')!;
const selRotY = document.getElementById('sel-rot-y')!;
const selRotZ = document.getElementById('sel-rot-z')!;
const selSizeX = document.getElementById('sel-size-x')!;
const selSizeY = document.getElementById('sel-size-y')!;
const selSizeZ = document.getElementById('sel-size-z')!;

const consolePanel = document.getElementById('console-panel')!;
const consoleOutput = document.getElementById('console-output')!;
const consoleClose = document.getElementById('console-close')!;

// --- Pastel color palette ---
const PASTEL_COLORS = [
  { name: 'none', hex: '' },
  { name: 'Rose', hex: '#FFB5BA' },
  { name: 'Coral', hex: '#FFB5B5' },
  { name: 'Blush', hex: '#FFD1DC' },
  { name: 'Pink', hex: '#FFB5E5' },
  { name: 'Hot Pink', hex: '#FF85A2' },
  { name: 'Salmon', hex: '#FFA07A' },
  { name: 'Peach', hex: '#FFDAB3' },
  { name: 'Apricot', hex: '#FFCBA4' },
  { name: 'Sand', hex: '#F5E6D3' },
  { name: 'Lemon', hex: '#FFF5BA' },
  { name: 'Gold', hex: '#FFE066' },
  { name: 'Cream', hex: '#FFFDD0' },
  { name: 'Mint', hex: '#B5FFCD' },
  { name: 'Sage', hex: '#C1E1C1' },
  { name: 'Lime', hex: '#CCFF90' },
  { name: 'Olive', hex: '#B5CC8E' },
  { name: 'Emerald', hex: '#7DCEA0' },
  { name: 'Teal', hex: '#80CBC4' },
  { name: 'Aqua', hex: '#B5FFF5' },
  { name: 'Sky', hex: '#B5E5FF' },
  { name: 'Powder', hex: '#B5D4FF' },
  { name: 'Steel', hex: '#88AACC' },
  { name: 'Cornflower', hex: '#93B8E6' },
  { name: 'Ice', hex: '#D6EEFF' },
  { name: 'Lavender', hex: '#D4B5FF' },
  { name: 'Lilac', hex: '#E5B5FF' },
  { name: 'Plum', hex: '#CC99CC' },
  { name: 'Violet', hex: '#B088CC' },
  { name: 'Mauve', hex: '#D4A5C4' },
  { name: 'Orchid', hex: '#DA9FE0' },
  { name: 'White', hex: '#FFFFFF' },
  { name: 'Pearl', hex: '#F0EDE5' },
  { name: 'Silver', hex: '#C8C8C8' },
  { name: 'Slate', hex: '#A0A0A0' },
  { name: 'Charcoal', hex: '#707070' },
  { name: 'Dark', hex: '#404040' },
];

// --- Title-bar menu dropdowns ---

const tbMenus = Array.from(document.querySelectorAll<HTMLElement>('.tb-menu'));
function closeAllTbMenus(): void {
  for (const m of tbMenus) m.classList.remove('open');
}
for (const menu of tbMenus) {
  const btn = menu.querySelector<HTMLButtonElement>('button')!;
  btn.addEventListener('click', (e) => {
    e.stopPropagation();
    const wasOpen = menu.classList.contains('open');
    closeAllTbMenus();
    if (!wasOpen) menu.classList.add('open');
  });
  // Clicking any .menu-item inside closes the menu
  menu.addEventListener('click', (e) => {
    const target = e.target as HTMLElement;
    if (target.classList.contains('menu-item') || target.closest('.menu-item')) {
      closeAllTbMenus();
    }
  });
}
document.addEventListener('click', () => closeAllTbMenus());

// --- Drawer controller ---

const drawer = document.getElementById('drawer')!;
const drawerTitle = document.getElementById('drawerTitle')!;
const drawerCloseBtn = document.getElementById('drawerClose')!;
const railBtns = document.querySelectorAll<HTMLButtonElement>('.rail button[data-drawer]');
const drawerPanels = document.querySelectorAll<HTMLElement>('.drawer-panel');
const drawerTitles: Record<string, string> = { files: 'Files', examples: 'Examples', params: 'Parameters', outline: 'Outline' };
let currentDrawer: string | null = null;

function openDrawer(key: string): void {
  if (currentDrawer === key) { closeDrawer(); return; }
  currentDrawer = key;
  drawer.classList.add('open');
  document.body.classList.add('drawer-open');
  drawerTitle.textContent = drawerTitles[key] || '';
  drawerPanels.forEach(p => p.classList.toggle('active', p.dataset.panel === key));
  railBtns.forEach(b => b.classList.toggle('active', b.dataset.drawer === key));
}
function closeDrawer(): void {
  currentDrawer = null;
  drawer.classList.remove('open');
  document.body.classList.remove('drawer-open');
  railBtns.forEach(b => b.classList.remove('active'));
}
railBtns.forEach(b => b.addEventListener('click', () => openDrawer(b.dataset.drawer!)));
drawerCloseBtn.addEventListener('click', closeDrawer);

// --- Examples: populate both title-bar menu and drawer panel ---

const examples = app.getExamples();
const menuExampleList = document.getElementById('menu-example-list')!;
const drawerExampleList = document.getElementById('drawer-example-list')!;

{
  const categoryOrder: string[] = [];
  const categoryExamples = new Map<string, { ex: typeof examples[0]; idx: number }[]>();
  examples.forEach((ex, i) => {
    const cat = ex.category || 'Other';
    if (!categoryExamples.has(cat)) {
      categoryOrder.push(cat);
      categoryExamples.set(cat, []);
    }
    categoryExamples.get(cat)!.push({ ex, idx: i });
  });

  function loadExampleAsNewProject(idx: number, name: string, code: string): void {
    const current = getActiveProject();
    current.code = app.getCode();
    const [cox, coy, coz] = app.getViewport().getUserOriginScad();
    if (Math.abs(cox) + Math.abs(coy) + Math.abs(coz) > 1e-6) {
      current.origin = [cox, coy, coz];
    } else {
      delete current.origin;
    }
    const newProj: Project = {
      id: Date.now().toString(36),
      name,
      code,
      isExample: true,
    };
    projects.push(newProj);
    activeProjectId = newProj.id;
    saveProjects();
    loadExampleClean(idx);
    app.getViewport().resetUserOrigin();
    renderTabs();
    window.umami?.track('load_example', { name });
  }

  for (const cat of categoryOrder) {
    // Title bar dropdown
    const header = document.createElement('div');
    header.className = 'menu-example-category';
    header.textContent = cat;
    menuExampleList.appendChild(header);

    // Drawer panel (collapsible sections)
    const drawerSec = document.createElement('div');
    drawerSec.className = 'sb-section';
    const drawerHead = document.createElement('div');
    drawerHead.className = 'sb-head';
    const drawerHeadSpan = document.createElement('span');
    drawerHeadSpan.textContent = cat;
    drawerHead.appendChild(drawerHeadSpan);
    drawerSec.appendChild(drawerHead);

    for (const { ex, idx } of categoryExamples.get(cat)!) {
      // Title bar
      const mBtn = document.createElement('button');
      mBtn.className = 'menu-example';
      mBtn.textContent = ex.name;
      mBtn.addEventListener('click', () => loadExampleAsNewProject(idx, ex.name, ex.code));
      menuExampleList.appendChild(mBtn);

      // Drawer
      const dItem = document.createElement('div');
      dItem.className = 'sb-sub';
      dItem.textContent = ex.name;
      dItem.addEventListener('click', () => loadExampleAsNewProject(idx, ex.name, ex.code));
      drawerSec.appendChild(dItem);
    }

    drawerExampleList.appendChild(drawerSec);
  }
}

// --- Project management with localStorage ---

interface Project {
  id: string;
  name: string;
  code: string;
  isExample?: boolean;
  dirty?: boolean;
  /** User-defined origin in SCAD coords (Z-up). Undefined = world (0,0,0). */
  origin?: [number, number, number];
  /** Token of a live Claude Code (MCP) session this tab is synced with. */
  live?: string;
  /** Local port of that session's MCP server (found by probing when the URL lacks &port). */
  livePort?: number;
}

const STORAGE_KEY = 'opencsg-projects';
const ACTIVE_KEY = 'opencsg-active-project';
const tabContainer = document.getElementById('tab-container')!;
const newProjectBtn = document.getElementById('new-project-btn')!;
const drawerProjectList = document.getElementById('drawer-project-list')!;
const drawerNewProject = document.getElementById('drawer-new-project')!;

function loadProjects(): Project[] {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (raw) {
      const parsed = JSON.parse(raw);
      if (Array.isArray(parsed) && parsed.length > 0) return parsed;
    }
  } catch { /* ignore */ }
  return [{ id: 'default', name: 'Untitled', code: examples[0].code }];
}

function saveProjects(): void {
  localStorage.setItem(STORAGE_KEY, JSON.stringify(projects));
  localStorage.setItem(ACTIVE_KEY, activeProjectId);
}

let projects = loadProjects();
let activeProjectId = localStorage.getItem(ACTIVE_KEY) || projects[0].id;
if (!projects.find(p => p.id === activeProjectId)) {
  activeProjectId = projects[0].id;
}

function getActiveProject(): Project {
  return projects.find(p => p.id === activeProjectId)!;
}

// --- Claude session state (see attachLive) ---
// Declared up here: renderTabs/syncTitlebarFilename run before the live code below.
type LiveState = 'connecting' | 'online' | 'offline';
/** The one project per page that is synced with a Claude session. */
let liveProjectId: string | null = null;
let liveEvents: EventSource | null = null;
let liveState: LiveState = 'offline';
let liveSession: { client?: string; folder?: string } = {};

function liveStateOf(proj: Project): LiveState | null {
  if (!proj.live) return null;
  return proj.id === liveProjectId ? liveState : 'offline';
}

function claudeAppName(client?: string): string {
  if (!client) return 'Claude';
  if (/claude-code/i.test(client)) return 'Claude Code';
  if (/claude-ai|claude desktop/i.test(client)) return 'Claude Desktop';
  return client;
}

/** Title-bar chip + dots on editor tabs: is this model working with a Claude session? */
function renderLiveIndicator(): void {
  const chip = document.getElementById('tb-claude') as HTMLButtonElement | null;
  const proj = projects.find(p => p.id === activeProjectId);
  const state = proj ? liveStateOf(proj) : null;
  if (chip) {
    chip.hidden = !state;
    chip.classList.toggle('online', state === 'online');
    chip.classList.toggle('connecting', state === 'connecting');
    const name = claudeAppName(liveSession.client);
    const where = liveSession.folder && /claude-code/i.test(liveSession.client ?? '') ? ` · ${liveSession.folder}` : '';
    chip.querySelector('.cc-label')!.textContent =
      state === 'online' ? `${name}${where}` : state === 'connecting' ? 'Connecting to Claude…' : 'Claude session ended';
    chip.title = state === 'online' ? `Working with ${name}${where ? ` in ${liveSession.folder}` : ''} — Claude's changes appear here, your edits go back to Claude. Click for options.`
      : state === 'connecting' ? 'Looking for the Claude session on this computer…'
      : 'This model was made with Claude; that session has ended. Click to continue in a new session.';
  }
  for (const tab of tabContainer.querySelectorAll<HTMLElement>('.project-tab')) {
    const p = projects.find(x => x.id === tab.dataset.id);
    const st = p ? liveStateOf(p) : null;
    let dot = tab.querySelector<HTMLElement>('.tab-live');
    if (!st) { dot?.remove(); continue; }
    if (!dot) { dot = document.createElement('span'); tab.prepend(dot); }
    dot.className = `tab-live ${st}`;
    dot.title = st === 'online' ? `Live with ${claudeAppName(liveSession.client)}` : st === 'connecting' ? 'Connecting to Claude…' : 'Claude session ended';
  }
}

function setLiveState(state: LiveState): void {
  liveState = state;
  const proj = projects.find(p => p.id === liveProjectId);
  if (proj) document.title = `${state === 'online' ? '●' : '○'} ${proj.name}${state === 'connecting' ? ' (connecting…)' : state === 'offline' ? ' (disconnected)' : ''} — SCAD+`;
  renderLiveIndicator();
}

function syncTitlebarFilename(): void {
  renderLiveIndicator();
  const proj = getActiveProject();
  if (tbFilename) tbFilename.textContent = `${proj.name}.scad`;
  if (tbModifiedDot) tbModifiedDot.classList.toggle('hidden', !proj.dirty);
  const ctxFn = document.querySelector('.ai-ctx-filename') as HTMLElement | null;
  if (ctxFn) ctxFn.textContent = `${proj.name}.scad`;
}

function renderDrawerProjectList(): void {
  drawerProjectList.innerHTML = '';
  for (const proj of projects) {
    const item = document.createElement('div');
    item.className = 'sb-item file' + (proj.id === activeProjectId ? ' active' : '');
    item.textContent = `${proj.name}.scad`;
    item.addEventListener('click', () => {
      if (proj.id !== activeProjectId) switchProject(proj.id);
    });
    drawerProjectList.appendChild(item);
  }
}

const tabOverflowWrap = document.getElementById('tab-overflow')!;
const tabOverflowBtn = document.getElementById('tab-overflow-btn')!;
const tabOverflowMenu = document.getElementById('tab-overflow-menu')!;

function updateTabOverflow(): void {
  // Show ... button only when some tabs are clipped
  const overflowing = tabContainer.scrollWidth > tabContainer.clientWidth + 1;
  tabOverflowWrap.style.display = overflowing ? 'inline-flex' : 'none';
  if (!overflowing) tabOverflowWrap.classList.remove('open');
}

function populateTabOverflowMenu(): void {
  tabOverflowMenu.innerHTML = '';
  for (const proj of projects) {
    const item = document.createElement('button');
    item.className = proj.id === activeProjectId ? 'active' : '';
    const label = document.createElement('span');
    label.textContent = proj.name + (proj.dirty ? ' •' : '');
    item.appendChild(label);
    const closeBtn = document.createElement('span');
    closeBtn.className = 'tab-close';
    closeBtn.textContent = '\u00d7';
    closeBtn.addEventListener('click', (e) => {
      e.stopPropagation();
      closeProject(proj.id);
      tabOverflowWrap.classList.remove('open');
    });
    item.appendChild(closeBtn);
    item.addEventListener('click', () => {
      if (proj.id !== activeProjectId) switchProject(proj.id);
      tabOverflowWrap.classList.remove('open');
    });
    tabOverflowMenu.appendChild(item);
  }
}

tabOverflowBtn.addEventListener('click', (e) => {
  e.stopPropagation();
  populateTabOverflowMenu();
  const willOpen = !tabOverflowWrap.classList.contains('open');
  tabOverflowWrap.classList.toggle('open');
  if (willOpen) {
    const r = tabOverflowBtn.getBoundingClientRect();
    tabOverflowMenu.style.top = r.bottom + 'px';
    tabOverflowMenu.style.right = 'auto';
    const menuWidth = Math.max(200, tabOverflowMenu.offsetWidth);
    // Prefer right-aligning with the button; if that pushes the menu off
    // the left edge, left-align to the button instead; finally clamp.
    let left = r.right - menuWidth;
    if (left < 8) left = Math.min(r.left, window.innerWidth - menuWidth - 8);
    left = Math.max(8, left);
    tabOverflowMenu.style.left = left + 'px';
  }
});
document.addEventListener('click', () => tabOverflowWrap.classList.remove('open'));

// Re-check overflow when the editor column resizes
const tabResizeObs = new ResizeObserver(() => updateTabOverflow());
tabResizeObs.observe(tabContainer);

function renderTabs(): void {
  tabContainer.innerHTML = '';
  for (const proj of projects) {
    const tab = document.createElement('div');
    tab.className = 'project-tab' + (proj.id === activeProjectId ? ' active' : '');
    tab.dataset.id = proj.id;

    const nameSpan = document.createElement('span');
    nameSpan.className = 'tab-name';
    nameSpan.textContent = proj.name;
    tab.appendChild(nameSpan);

    const closeBtn = document.createElement('button');
    closeBtn.className = 'tab-close';
    closeBtn.textContent = '\u00d7';
    tab.appendChild(closeBtn);

    tab.addEventListener('click', (e) => {
      if ((e.target as HTMLElement).classList.contains('tab-close')) return;
      if (proj.id === activeProjectId) return;
      switchProject(proj.id);
    });

    nameSpan.addEventListener('dblclick', (e) => {
      e.stopPropagation();
      nameSpan.contentEditable = 'true';
      nameSpan.focus();
      const range = document.createRange();
      range.selectNodeContents(nameSpan);
      const sel = window.getSelection();
      sel?.removeAllRanges();
      sel?.addRange(range);
    });

    nameSpan.addEventListener('blur', () => {
      nameSpan.contentEditable = 'false';
      const newName = nameSpan.textContent?.trim() || 'Untitled';
      proj.name = newName;
      nameSpan.textContent = newName;
      saveProjects();
      syncTitlebarFilename();
      renderDrawerProjectList();
    });

    nameSpan.addEventListener('keydown', (e) => {
      if (e.key === 'Enter') { e.preventDefault(); nameSpan.blur(); }
      else if (e.key === 'Escape') {
        nameSpan.textContent = proj.name;
        nameSpan.contentEditable = 'false';
      }
    });

    closeBtn.addEventListener('click', (e) => {
      e.stopPropagation();
      closeProject(proj.id);
    });

    tabContainer.appendChild(tab);
  }
  renderDrawerProjectList();
  syncTitlebarFilename();
  // Defer to next frame so DOM has laid out
  requestAnimationFrame(() => {
    updateTabOverflow();
    // Scroll the active tab into view so selecting a clipped tab reveals it
    const activeTab = tabContainer.querySelector('.project-tab.active') as HTMLElement | null;
    if (activeTab) {
      activeTab.scrollIntoView({ block: 'nearest', inline: 'center' });
    }
  });
}

function switchProject(id: string): void {
  const current = getActiveProject();
  current.code = app.getCode();
  // Snapshot current project's origin before switching
  const [cox, coy, coz] = app.getViewport().getUserOriginScad();
  if (Math.abs(cox) + Math.abs(coy) + Math.abs(coz) > 1e-6) {
    current.origin = [cox, coy, coz];
  } else {
    delete current.origin;
  }
  activeProjectId = id;
  saveProjects();
  const proj = getActiveProject();
  pendingAutoFit = true; // re-fit viewport + grid to the new model once it renders
  loadCodeClean(proj.code);
  // Apply new project's origin (or reset to world if none saved)
  if (proj.origin) {
    app.getViewport().setUserOriginScad(proj.origin[0], proj.origin[1], proj.origin[2]);
  } else {
    app.getViewport().resetUserOrigin();
  }
  renderTabs();
}

function closeProject(id: string): void {
  const closing = projects.find(p => p.id === id);
  if (!closing) return;

  if (!closing.isExample && closing.dirty) {
    if (id === activeProjectId) closing.code = app.getCode();
    const action = confirm(`Save "${closing.name}" before closing?\n\nOK = Save as .scad file, then close\nCancel = Close without saving`);
    if (action) {
      const blob = new Blob([closing.code], { type: 'text/plain' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = (closing.name || 'model') + '.scad';
      a.click();
      URL.revokeObjectURL(url);
    }
  }

  const wasActive = id === activeProjectId;

  if (projects.length === 1) {
    const newProj: Project = { id: Date.now().toString(36), name: 'Untitled', code: 'cube([20, 20, 20], center=true);' };
    projects.push(newProj);
    activeProjectId = newProj.id;
  } else if (wasActive) {
    const idx = projects.findIndex(p => p.id === id);
    const nextIdx = idx > 0 ? idx - 1 : 1;
    activeProjectId = projects[nextIdx].id;
  }

  projects = projects.filter(p => p.id !== id);
  saveProjects();

  if (wasActive) {
    loadCodeClean(getActiveProject().code);
    const proj = getActiveProject();
    if (proj.origin) {
      app.getViewport().setUserOriginScad(proj.origin[0], proj.origin[1], proj.origin[2]);
    } else {
      app.getViewport().resetUserOrigin();
    }
  }
  renderTabs();
}

function newProject(): void {
  const name = prompt('Project name:', 'Untitled');
  if (name === null) return;
  const current = getActiveProject();
  current.code = app.getCode();
  // Snapshot outgoing project's origin
  const [cox, coy, coz] = app.getViewport().getUserOriginScad();
  if (Math.abs(cox) + Math.abs(coy) + Math.abs(coz) > 1e-6) {
    current.origin = [cox, coy, coz];
  } else {
    delete current.origin;
  }
  const newProj: Project = { id: Date.now().toString(36), name: name || 'Untitled', code: 'cube([20, 20, 20], center=true);' };
  projects.push(newProj);
  activeProjectId = newProj.id;
  saveProjects();
  loadCodeClean(newProj.code);
  app.getViewport().resetUserOrigin();
  renderTabs();
}
newProjectBtn.addEventListener('click', newProject);
drawerNewProject.addEventListener('click', newProject);

// Track programmatic loads so the code-change callback doesn't mark dirty
let suppressDirty = false;
function loadCodeClean(code: string): void {
  suppressDirty = true;
  app.loadCode(code);
}
function loadExampleClean(idx: number): void {
  suppressDirty = true;
  app.loadExample(idx);
}

app.onCodeUpdate((code) => {
  const proj = getActiveProject();
  proj.code = code;
  if (suppressDirty) {
    suppressDirty = false;
  } else {
    if (!proj.dirty) {
      proj.dirty = true;
      syncTitlebarFilename();
    }
    if (proj.isExample) proj.isExample = false;
  }
  saveProjects();
  // Keep customizer + outline in sync (functions hoisted below)
  renderCustomizer(code);
  renderOutline();
  // Live session: send the user's edits back to the local MCP server
  if (proj.live && proj.id === liveProjectId && liveBase !== null && code !== liveLastSynced) {
    liveLastSynced = code;
    void fetch(`${liveBase}/live/code?token=${encodeURIComponent(proj.live)}`, {
      method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ code }),
    }).catch(() => { /* server gone — the tab keeps working offline */ });
  }
});

window.addEventListener('beforeunload', (e) => {
  const current = getActiveProject();
  current.code = app.getCode();
  saveProjects();
  if (projects.some(p => p.dirty && !p.isExample)) e.preventDefault();
});

// Live session with a local Claude Code MCP server: ?live=<token>
// One tab per session; code pushed by Claude replaces the editor content as
// an undoable edit, the user's own edits are POSTed back (see onCodeUpdate).
let liveLastSynced: string | null = null;
const liveParams = new URLSearchParams(window.location.search);
const liveToken = liveParams.get('live');
// &port=N → the MCP server runs on this computer (the page itself may come
// from the public site); without it the page is served by that server — or
// the link lost its &port (AI apps sometimes shorten it), see findLiveBase.
const livePortParam = liveParams.get('port');
const LIVE_PORTS = Array.from({ length: 21 }, (_, i) => 47800 + i); // mcp/server.ts tries 47800..47820

/** Where this session's MCP server answers: '' = same origin, else http://127.0.0.1:PORT. */
async function findLiveBase(token: string, savedPort?: number): Promise<{ base: string; port?: number } | null> {
  const hello = async (base: string) => {
    try {
      const r = await fetch(`${base}/live/hello?token=${encodeURIComponent(token)}`, { cache: 'no-store', signal: AbortSignal.timeout(4000) });
      return r.status === 204; // the site's SPA fallback answers 200 with HTML
    } catch { return false; }
  };
  if (livePortParam && /^\d{2,5}$/.test(livePortParam)) return { base: `http://127.0.0.1:${livePortParam}`, port: Number(livePortParam) };
  if (await hello('')) return { base: '' };
  const candidates = [...new Set([...(savedPort ? [savedPort] : []), ...LIVE_PORTS])];
  const found = await Promise.all(candidates.map(async p => (await hello(`http://127.0.0.1:${p}`)) ? p : null));
  const port = found.find((p): p is number => p !== null);
  return port ? { base: `http://127.0.0.1:${port}`, port } : null;
}
/** Set once findLiveBase succeeds; user edits are POSTed here. */
let liveBase: string | null = null;

/** Sync `target` with the MCP session `token` at `base` (Claude's versions in, the user's edits out). */
function attachLive(target: Project, token: string, base: string, port?: number): void {
  liveEvents?.close();
  liveBase = base;
  liveProjectId = target.id;
  liveSession = {};
  setLiveState('connecting');
  target.live = token;
  if (port) target.livePort = port;
  saveProjects();
  // The URL names the session, so a reload (or a copied link) reconnects directly
  const u = new URL(window.location.href);
  u.searchParams.set('live', token);
  if (port) u.searchParams.set('port', String(port));
  history.replaceState(null, '', u.toString());
  liveLastSynced = target.code; // don't echo what the server already has
  // Nothing real to undo back to until the first version from Claude arrives
  let placeholder = !target.code.trim() || target.code.startsWith('// Waiting for Claude');
  const events = new EventSource(`${base}/live/events?token=${encodeURIComponent(token)}`);
  liveEvents = events;
  events.addEventListener('code', (ev) => {
    const { code } = JSON.parse((ev as MessageEvent).data) as { code: string };
    if (code === target.code && (activeProjectId !== target.id || code === app.getCode())) return;
    liveLastSynced = code;
    target.code = code;
    target.dirty = false;
    saveProjects();
    if (activeProjectId === target.id) {
      if (placeholder) loadCodeClean(code); // replace the placeholder, no undo step
      else app.applyCodeChange(code);      // undoable: Ctrl+Z steps back through Claude's versions
      renderCustomizer(code);
      renderOutline();
    }
    placeholder = false;
  });
  events.addEventListener('session', (ev) => {
    try { liveSession = JSON.parse((ev as MessageEvent).data); } catch { liveSession = {}; }
    renderLiveIndicator();
  });
  // EventSource retries on its own: show "connecting" while it does, and give up
  // (session ended → offer a new one) when it's still down after 8 s.
  let lostTimer: ReturnType<typeof setTimeout> | undefined;
  let gaveUp = false;
  const sessionEnded = () => {
    gaveUp = true;
    setLiveState('offline');
    showSessionEnded(target);
  };
  events.addEventListener('open', () => {
    gaveUp = false;
    clearTimeout(lostTimer);
    lostTimer = undefined;
    setLiveState('online');
  });
  events.addEventListener('error', () => {
    if (liveEvents !== events) return;
    if (events.readyState === EventSource.CLOSED) { if (!gaveUp) sessionEnded(); return; }
    if (!gaveUp) setLiveState('connecting');
    lostTimer ??= setTimeout(() => {
      lostTimer = undefined;
      if (liveEvents === events && events.readyState !== EventSource.OPEN && !gaveUp) sessionEnded();
    }, 8000);
  });
}

/** Stop syncing this model with Claude (the model stays as it is). */
function detachLive(): void {
  liveEvents?.close();
  liveEvents = null;
  const proj = projects.find(p => p.id === liveProjectId);
  if (proj) { delete proj.live; delete proj.livePort; saveProjects(); }
  liveProjectId = null;
  liveBase = null;
  const u = new URL(window.location.href);
  u.searchParams.delete('live');
  u.searchParams.delete('port');
  history.replaceState(null, '', u.toString());
  document.title = 'SCAD+';
  renderLiveIndicator();
}

document.getElementById('tb-claude')!.addEventListener('click', () => {
  const proj = getActiveProject();
  const state = liveStateOf(proj);
  if (state === 'online') {
    const name = claudeAppName(liveSession.client);
    const where = liveSession.folder && /claude-code/i.test(liveSession.client ?? '') ? ` in <strong>${escapeHtml(liveSession.folder)}</strong>` : '';
    showClaudePanel(`<strong>Live with ${name}</strong>${where}.<span class="cp-hint">Claude's changes appear in this tab; your edits go back to Claude.</span>`, [
      { label: 'Disconnect', onClick: () => { detachLive(); closeClaudePanel(); showToast('Disconnected from Claude'); } },
      { label: 'OK', primary: true, onClick: closeClaudePanel },
    ]);
  } else if (state === 'offline') {
    showSessionEnded(proj, true);
  }
});

// --- "Open in Claude": continue a model in a new Claude session ---
// The deep link pre-fills a prompt with a pairing code; Claude passes it to the
// MCP tool connect_scadplus_tab, which answers /live/pair?pair=<code> on its
// local port. This tab polls the MCP port range for it, then sends its model
// and attaches itself to that session.

type ClaudeTarget = 'code' | 'desktop' | 'copy';
const CLAUDE_FOLDER_KEY = 'scadplus-claude-folder';
let pairingCancel: (() => void) | null = null;

function claudePrompt(name: string, pair: string): string {
  return `Continue my SCAD+ model "${name}" — connect to the SCAD+ tab with the scadplus tool connect_scadplus_tab (tab: ${pair}). `;
}

function claudeDeepLink(via: 'code' | 'desktop', prompt: string): string {
  let folder = '';
  try { folder = localStorage.getItem(CLAUDE_FOLDER_KEY) ?? ''; } catch { /* ignore */ }
  const q = `q=${encodeURIComponent(prompt)}`;
  if (via === 'code') return `claude-cli://open?${q}${folder ? `&cwd=${encodeURIComponent(folder)}` : ''}`;
  return `claude://code/new?${q}${folder ? `&folder=${encodeURIComponent(folder)}` : ''}`;
}

function showClaudePanel(html: string, buttons: Array<{ label: string; primary?: boolean; onClick: () => void }>, spinner = false): HTMLElement {
  document.getElementById('claude-pair')?.remove();
  const panel = document.createElement('div');
  panel.id = 'claude-pair';
  panel.className = 'claude-pair';
  panel.innerHTML = `${spinner ? '<span class="cp-spin"></span>' : ''}<span class="cp-text">${html}</span>`;
  for (const b of buttons) {
    const el = document.createElement('button');
    el.textContent = b.label;
    if (b.primary) el.className = 'cp-primary';
    el.addEventListener('click', b.onClick);
    panel.appendChild(el);
  }
  panel.querySelector('[data-mcp-help]')?.addEventListener('click', () => showHelpTab('mcp'));
  document.body.appendChild(panel);
  return panel;
}
const closeClaudePanel = () => document.getElementById('claude-pair')?.remove();

/** Ask every MCP port whether a session is waiting for `pair`. */
async function findPairedSession(pair: string): Promise<{ token: string; port: number } | null> {
  const hits = await Promise.all(LIVE_PORTS.map(async (port) => {
    try {
      const r = await fetch(`http://127.0.0.1:${port}/live/pair?pair=${encodeURIComponent(pair)}`, { cache: 'no-store', signal: AbortSignal.timeout(1500) });
      if (r.status !== 200) return null;
      const { token } = await r.json() as { token?: string };
      return typeof token === 'string' ? { token, port } : null;
    } catch { return null; }
  }));
  return hits.find((h): h is { token: string; port: number } => h !== null) ?? null;
}

function openInClaude(via: ClaudeTarget): void {
  pairingCancel?.();
  const target = getActiveProject();
  const pair = Array.from(crypto.getRandomValues(new Uint8Array(8)), b => 'abcdefghjkmnpqrstuvwxyz23456789'[b % 31]).join('');
  const prompt = claudePrompt(target.name, pair);
  if (via === 'copy') {
    navigator.clipboard.writeText(prompt).catch(() => { /* shown in the panel below anyway */ });
  } else {
    // A custom scheme doesn't navigate the page away; the browser asks to open the app
    const a = document.createElement('a');
    a.href = claudeDeepLink(via, prompt);
    a.click();
  }
  const appName = via === 'desktop' ? 'Claude Desktop' : via === 'code' ? 'Claude Code' : 'Claude';
  const waitingHtml = (late: boolean) =>
    `<strong>Waiting for ${appName}…</strong>` +
    (via === 'copy' ? ` Prompt copied — paste it into Claude and press Enter.` : ` Press Enter in ${appName} to connect this model.`) +
    (late ? `<span class="cp-hint">Nothing yet? Claude needs the SCAD+ MCP server — <a data-mcp-help>set it up</a>. Allow access to the local network if the browser asks.</span>` : '');
  let cancelled = false;
  const started = Date.now();
  let late = false;
  const buttons = [{ label: 'Cancel', onClick: () => { pairingCancel?.(); closeClaudePanel(); } }];
  showClaudePanel(waitingHtml(false), buttons, true);
  pairingCancel = () => { cancelled = true; pairingCancel = null; };
  const poll = async () => {
    if (cancelled) return;
    const hit = await findPairedSession(pair);
    if (cancelled) return;
    if (hit) {
      pairingCancel = null;
      const base = `http://127.0.0.1:${hit.port}`;
      try {
        await fetch(`${base}/live/code?token=${encodeURIComponent(hit.token)}`, {
          method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ code: activeProjectId === target.id ? app.getCode() : target.code, joined: true }),
        });
      } catch { /* attach anyway; Claude can still push its version */ }
      attachLive(target, hit.token, base, hit.port);
      showClaudePanel(`<strong>Connected to ${appName}.</strong> This model now follows the conversation.`, [{ label: 'OK', onClick: closeClaudePanel }]);
      setTimeout(closeClaudePanel, 6000);
      return;
    }
    if (Date.now() - started > 5 * 60_000) {
      pairingCancel = null;
      showClaudePanel(`<strong>${appName} didn't connect.</strong><span class="cp-hint">Check that the SCAD+ MCP server is installed — <a data-mcp-help>setup</a> — and try again.</span>`,
        [{ label: 'Try again', primary: true, onClick: () => openInClaude(via) }, { label: 'Close', onClick: closeClaudePanel }]);
      return;
    }
    if (!late && Date.now() - started > 60_000) { late = true; showClaudePanel(waitingHtml(true), buttons, true); }
    // Every probe of a closed port is a console error — ease off after the first minute
    setTimeout(poll, late ? 5000 : 2000);
  };
  setTimeout(poll, 1000);
}

/** A live tab whose Claude session is gone: offer to continue in a new one. */
function showSessionEnded(target: Project, withForget = false): void {
  if (pairingCancel || activeProjectId !== target.id) return;
  showClaudePanel(`<strong>The Claude session for "${escapeHtml(target.name)}" has ended.</strong><span class="cp-hint">Continue this model in a new session:</span>`, [
    { label: 'Claude Code', primary: true, onClick: () => openInClaude('code') },
    { label: 'Claude Desktop', onClick: () => openInClaude('desktop') },
    { label: 'Copy prompt', onClick: () => openInClaude('copy') },
    ...(withForget ? [{ label: 'Forget session', onClick: () => { liveProjectId = target.id; detachLive(); closeClaudePanel(); } }] : []),
    { label: '×', onClick: closeClaudePanel },
  ]);
}

document.getElementById('menu-claude-code')!.addEventListener('click', () => openInClaude('code'));
document.getElementById('menu-claude-desktop')!.addEventListener('click', () => openInClaude('desktop'));
document.getElementById('menu-claude-copy')!.addEventListener('click', () => openInClaude('copy'));
document.getElementById('menu-claude-folder')!.addEventListener('click', () => {
  let current = '';
  try { current = localStorage.getItem(CLAUDE_FOLDER_KEY) ?? ''; } catch { /* ignore */ }
  const v = window.prompt('Folder Claude Code should start in (absolute path; empty = Claude\'s default):', current);
  if (v === null) return;
  try { v.trim() ? localStorage.setItem(CLAUDE_FOLDER_KEY, v.trim()) : localStorage.removeItem(CLAUDE_FOLDER_KEY); } catch { /* ignore */ }
  showToast(v.trim() ? `Claude Code will start in ${v.trim()}` : 'Claude Code will start in its default folder');
});
document.getElementById('menu-claude-help')!.addEventListener('click', () => showHelpTab('mcp'));

// Initial load
const sharedCode = decodeScadFromHash(window.location.hash);
if (liveToken) {
  let liveProj = projects.find(p => p.live === liveToken);
  if (!liveProj) {
    liveProj = { id: Date.now().toString(36), name: 'Claude session', code: '// Waiting for Claude…\n', live: liveToken };
    projects.push(liveProj);
  }
  activeProjectId = liveProj.id;
  saveProjects();
  loadCodeClean(liveProj.code);
  const target = liveProj;
  const token = liveToken;
  liveProjectId = target.id;
  setLiveState('connecting');
  findLiveBase(token, target.livePort).then((found) => {
    if (!found) {
      setLiveState('offline');
      showSessionEnded(target);
      return;
    }
    attachLive(target, token, found.base, found.port);
  });
} else if (sharedCode) {
  const sharedProj: Project = { id: Date.now().toString(36), name: 'Shared Design', code: sharedCode };
  projects.push(sharedProj);
  activeProjectId = sharedProj.id;
  saveProjects();
  history.replaceState(null, '', window.location.pathname + window.location.search);
  loadCodeClean(sharedProj.code);
} else {
  loadCodeClean(getActiveProject().code);
}
renderTabs();

// --- Console strip + expanded console panel ---

let aiPanelRef: AIChatPanel | null = null;

function setConsoleStrip(msg: string, severity: 'ok' | 'warning' | 'error'): void {
  consoleStripMsg.textContent = msg;
  consoleStripEl.classList.toggle('has-error', severity === 'error');
  consoleStripEl.classList.toggle('has-warning', severity === 'warning');
}

const consoleFixAi = document.getElementById('console-fix-ai') as HTMLButtonElement;
consoleFixAi.addEventListener('click', (e) => {
  e.stopPropagation(); // the strip itself toggles the console panel
  aiPanelRef?.fixErrors();
});

app.onErrorUpdate((errors: ScadError[], echoLog?: string[]) => {
  if (aiPanelRef) {
    aiPanelRef.setErrors(errors);
    aiPanelRef.setEchoLog(echoLog ?? []);
  }
  consoleFixAi.hidden = !errors.some(e => e.severity !== 'warning');
  latestErrors = errors;
  renderOutline();
  consoleOutput.innerHTML = '';

  if (errors.length === 0 && (!echoLog || echoLog.length === 0)) {
    consolePanel.classList.remove('open');
    setConsoleStrip('Ready.', 'ok');
    syncConsoleStripUI();
    return;
  }

  if (echoLog) {
    for (const msg of echoLog) {
      const line = document.createElement('div');
      line.className = 'console-echo console-line';
      line.appendChild(document.createTextNode(msg));
      consoleOutput.appendChild(line);
    }
  }
  for (const err of errors) {
    const line = document.createElement('div');
    line.className = (err.severity === 'warning' ? 'console-warning' : 'console-error') + ' console-line';
    line.addEventListener('click', () => app.goToLine(err.from.line, err.from.col));
    const lineInfo = document.createElement('span');
    lineInfo.className = 'console-line-info';
    lineInfo.textContent = `Ln ${err.from.line}:${err.from.col}`;
    line.appendChild(lineInfo);
    line.appendChild(document.createTextNode(err.message));
    consoleOutput.appendChild(line);
  }
  consoleOutput.scrollTop = consoleOutput.scrollHeight;

  // Auto-open full panel on errors/echoes
  consolePanel.classList.add('open');
  syncConsoleStripUI();

  // Strip summary
  const err = errors.find(e => e.severity !== 'warning');
  const warn = errors.find(e => e.severity === 'warning');
  if (err) {
    setConsoleStrip(`Ln ${err.from.line}:${err.from.col}  ${err.message}`, 'error');
  } else if (warn) {
    setConsoleStrip(`Ln ${warn.from.line}:${warn.from.col}  ${warn.message}`, 'warning');
  } else if (echoLog && echoLog.length) {
    setConsoleStrip(echoLog[echoLog.length - 1], 'ok');
  }
});

function syncConsoleStripUI(): void {
  const open = consolePanel.classList.contains('open');
  consoleStripEl.classList.toggle('panel-open', open);
  const t = document.getElementById('console-strip-toggle');
  if (t) t.textContent = open ? '\u25B4' : '\u25BE';
}

consoleClose.addEventListener('click', () => {
  consolePanel.classList.remove('open');
  syncConsoleStripUI();
});

const railConsoleToggle = document.getElementById('rail-console-toggle')!;
function toggleConsolePanel(): void {
  consolePanel.classList.toggle('open');
  syncConsoleStripUI();
}
// Any click on the strip (not in a button) toggles. The chevron button also toggles.
consoleStripEl.addEventListener('click', toggleConsolePanel);
railConsoleToggle.addEventListener('click', toggleConsolePanel);
syncConsoleStripUI();

// --- About modal ---

const aboutOverlay = document.getElementById('about-modal-overlay')!;
const aboutClose = document.getElementById('about-modal-close')!;
function openAbout(): void { aboutOverlay.classList.add('open'); }
function closeAbout(): void { aboutOverlay.classList.remove('open'); }
aboutClose.addEventListener('click', closeAbout);
aboutOverlay.addEventListener('click', (e) => { if (e.target === aboutOverlay) closeAbout(); });
document.getElementById('menu-about')!.addEventListener('click', openAbout);

// --- Help modal ---

const helpOverlay = document.getElementById('help-modal-overlay')!;
const helpClose = document.getElementById('help-modal-close')!;
const helpTabBtns = helpOverlay.querySelectorAll('.help-tab-btn');
const helpSections = helpOverlay.querySelectorAll('.help-section');
function openHelp(): void { helpOverlay.classList.add('open'); }
function closeHelp(): void { helpOverlay.classList.remove('open'); }
helpClose.addEventListener('click', closeHelp);
helpOverlay.addEventListener('click', (e) => { if (e.target === helpOverlay) closeHelp(); });
helpTabBtns.forEach((btn) => {
  btn.addEventListener('click', () => {
    const tab = (btn as HTMLElement).dataset.tab!;
    helpTabBtns.forEach(b => b.classList.remove('active'));
    btn.classList.add('active');
    helpSections.forEach(s => s.classList.toggle('active', s.id === `help-${tab}`));
  });
});
document.getElementById('menu-help')!.addEventListener('click', openHelp);
function showHelpTab(tab: string): void {
  (helpOverlay.querySelector(`.help-tab-btn[data-tab="${tab}"]`) as HTMLElement | null)?.click();
  openHelp();
}
document.getElementById('menu-mcp')!.addEventListener('click', () => showHelpTab('mcp'));
helpOverlay.querySelectorAll<HTMLButtonElement>('.mcp-copy').forEach((btn) => {
  btn.addEventListener('click', () => {
    const text = btn.parentElement!.querySelector('code')!.textContent ?? '';
    navigator.clipboard.writeText(text).then(() => showToast('Copied!'))
      .catch(() => showToast('Copy failed — select the text and copy it manually'));
  });
});

// --- Transform modes ---

const MODE_LABELS: Record<string, string> = {
  translate: 'Translate (T)',
  rotate: 'Rotate (R) · Shift=45°',
  scale: 'Scale (S)',
};

const modeTranslateBtn = document.getElementById('mode-translate') as HTMLButtonElement;
const modeRotateBtn = document.getElementById('mode-rotate') as HTMLButtonElement;
const modeScaleBtn = document.getElementById('mode-scale') as HTMLButtonElement;
const modeBtns: Record<string, HTMLButtonElement> = {
  translate: modeTranslateBtn,
  rotate: modeRotateBtn,
  scale: modeScaleBtn,
};

function updateModeIndicator(mode: string, selected: boolean): void {
  if (selected) {
    transformModeEl.textContent = MODE_LABELS[mode] || mode;
    transformModeEl.style.display = 'block';
  } else {
    transformModeEl.style.display = 'none';
  }
  for (const [m, btn] of Object.entries(modeBtns)) {
    btn.classList.toggle('active', m === mode);
  }
}

viewport.onModeChange(updateModeIndicator);

modeTranslateBtn.addEventListener('click', () => viewport.setTransformMode('translate'));
modeRotateBtn.addEventListener('click', () => viewport.setTransformMode('rotate'));
modeScaleBtn.addEventListener('click', () => viewport.setTransformMode('scale'));

// --- Snap-to-grid ---

const snapToggle = document.getElementById('snap-toggle') as HTMLInputElement;
const snapStep = document.getElementById('snap-step') as HTMLInputElement;
snapToggle.addEventListener('change', () => viewport.setSnapSettings(snapToggle.checked, parseFloat(snapStep.value) || 0.1));
snapStep.addEventListener('input', () => viewport.setSnapSettings(snapToggle.checked, parseFloat(snapStep.value) || 0.1));

// --- Axis lock buttons ---

const axisLockX = document.getElementById('axis-lock-x') as HTMLButtonElement;
const axisLockY = document.getElementById('axis-lock-y') as HTMLButtonElement;
const axisLockZ = document.getElementById('axis-lock-z') as HTMLButtonElement;

function syncAxisLock(): void {
  viewport.setAxisLock(
    axisLockX.classList.contains('active'),
    axisLockY.classList.contains('active'),
    axisLockZ.classList.contains('active'),
  );
}

for (const btn of [axisLockX, axisLockY, axisLockZ]) {
  btn.addEventListener('click', () => {
    btn.classList.toggle('active');
    syncAxisLock();
  });
}

let kbdConstrainedScadAxis: 'x' | 'y' | 'z' | null = null;
function updateAxisConstraintUI(): void {
  const mapping: Array<['x' | 'y' | 'z', HTMLButtonElement]> = [
    ['x', axisLockX], ['y', axisLockY], ['z', axisLockZ],
  ];
  for (const [axis, btn] of mapping) {
    btn.classList.remove('kbd-active', 'kbd-dimmed');
    if (kbdConstrainedScadAxis !== null) {
      btn.classList.add(axis === kbdConstrainedScadAxis ? 'kbd-active' : 'kbd-dimmed');
    }
  }
}

window.addEventListener('pointerup', () => {
  if (kbdConstrainedScadAxis !== null) {
    setTimeout(() => {
      if (!viewport.isDimensionDragging()) {
        kbdConstrainedScadAxis = null;
        updateAxisConstraintUI();
      }
    }, 0);
  }
});

// --- $fn chips ---

const fnSelect = document.getElementById('fn-select') as HTMLSelectElement;
const fnChips = document.querySelectorAll<HTMLButtonElement>('#tg-fn button[data-fn]');
function setFn(n: number): void {
  fnSelect.value = String(n);
  app.setFn(n);
  vpFnLabel.textContent = `$fn ${n}`;
  fnChips.forEach(c => c.classList.toggle('active', parseInt(c.dataset.fn!, 10) === n));
}
for (const chip of fnChips) {
  chip.addEventListener('click', () => setFn(parseInt(chip.dataset.fn!, 10)));
}
// Reflect initial value
vpFnLabel.textContent = `$fn ${fnSelect.value}`;

// --- Parameter panel ---

const ANCHOR_NAMES = ['CENTER', 'TOP', 'BOTTOM', 'LEFT', 'RIGHT', 'FRONT', 'BACK'];
const AXIS_NAMES = ['X', 'Y', 'Z', 'XY', 'XZ', 'YZ'];

function renderParamRow(param: ParamInfo['params'][0], _moduleName: string): HTMLElement {
  const row = document.createElement('div');
  row.className = 'param-row';

  const label = document.createElement('label');
  label.textContent = param.name;
  row.appendChild(label);

  if (param.type === 'number' || param.type === 'vector') {
    const input = document.createElement('input');
    input.type = 'text';
    input.value = param.value;
    input.addEventListener('change', () => app.updateParam(param.from, param.to, input.value));
    input.addEventListener('keydown', (e) => { if (e.key === 'Enter') input.blur(); });
    row.appendChild(input);
  } else if (param.type === 'boolean') {
    const cb = document.createElement('input');
    cb.type = 'checkbox';
    cb.checked = param.value === 'true';
    cb.addEventListener('change', () => app.updateParam(param.from, param.to, cb.checked ? 'true' : 'false'));
    row.appendChild(cb);
  } else if (param.type === 'anchor') {
    const activeAnchors = new Set(
      param.value.split('+').map(s => s.trim()).filter(s => ANCHOR_NAMES.includes(s))
    );
    const group = document.createElement('div');
    group.className = 'anchor-group';
    for (const anchor of ANCHOR_NAMES) {
      const btn = document.createElement('button');
      btn.className = 'anchor-btn' + (activeAnchors.has(anchor) ? ' active' : '');
      btn.textContent = anchor;
      btn.addEventListener('click', () => {
        const updated = new Set(activeAnchors);
        if (updated.has(anchor)) {
          if (updated.size > 1) updated.delete(anchor);
        } else {
          updated.add(anchor);
        }
        const newValue = ANCHOR_NAMES.filter(a => updated.has(a)).join(' + ');
        app.updateParam(param.from, param.to, newValue);
      });
      group.appendChild(btn);
    }
    row.appendChild(group);
  } else if (param.type === 'axis') {
    const group = document.createElement('div');
    group.className = 'anchor-group';
    for (const axis of AXIS_NAMES) {
      const btn = document.createElement('button');
      btn.className = 'anchor-btn' + (axis === param.value ? ' active' : '');
      btn.textContent = axis;
      btn.addEventListener('click', () => app.updateParam(param.from, param.to, axis));
      group.appendChild(btn);
    }
    row.appendChild(group);
  } else {
    const input = document.createElement('input');
    input.type = 'text';
    input.value = param.value;
    input.readOnly = true;
    row.appendChild(input);
  }

  return row;
}

function createColorPalette(currentColor: string | null, currentOpacity: number): HTMLElement {
  const container = document.createElement('div');

  const colorHeader = document.createElement('div');
  colorHeader.className = 'param-section-header';
  colorHeader.textContent = 'Color';
  container.appendChild(colorHeader);

  const opacitySlider = document.createElement('input');
  opacitySlider.type = 'range';
  opacitySlider.min = '0';
  opacitySlider.max = '100';
  opacitySlider.value = String(Math.round(currentOpacity * 100));

  const palette = document.createElement('div');
  palette.className = 'color-palette';

  for (const color of PASTEL_COLORS) {
    const swatch = document.createElement('div');
    swatch.className = 'color-swatch';
    if (color.hex === '') swatch.classList.add('color-none');
    else swatch.style.backgroundColor = color.hex;
    swatch.title = color.name;

    if (currentColor && color.hex && color.hex.toLowerCase() === currentColor.toLowerCase()) {
      swatch.classList.add('active');
    }

    swatch.addEventListener('click', () => {
      palette.querySelectorAll('.color-swatch').forEach(s => s.classList.remove('active'));
      swatch.classList.add('active');
      const currentSliderOpacity = parseInt(opacitySlider.value) / 100;
      applyColorToSelection(color.hex === '' ? null : color.hex, currentSliderOpacity);
    });

    palette.appendChild(swatch);
  }
  container.appendChild(palette);

  const opacityRow = document.createElement('div');
  opacityRow.className = 'opacity-slider-row';
  const opacityLabel = document.createElement('label');
  opacityLabel.textContent = 'Opacity';
  opacityRow.appendChild(opacityLabel);
  opacityRow.appendChild(opacitySlider);
  const opacityValue = document.createElement('span');
  opacityValue.textContent = `${Math.round(currentOpacity * 100)}%`;
  opacityRow.appendChild(opacityValue);

  let opacityTimeout: number | null = null;
  opacitySlider.addEventListener('input', () => {
    const opacity = parseInt(opacitySlider.value) / 100;
    opacityValue.textContent = `${opacitySlider.value}%`;
    if (opacityTimeout) clearTimeout(opacityTimeout);
    opacityTimeout = window.setTimeout(() => {
      const activeSwatch = palette.querySelector('.color-swatch.active');
      const colorIndex = Array.from(palette.children).indexOf(activeSwatch as Element);
      let selectedColor = colorIndex >= 0 && PASTEL_COLORS[colorIndex].hex ? PASTEL_COLORS[colorIndex].hex : null;
      if (!selectedColor && currentColor) selectedColor = currentColor;
      if (selectedColor) applyColorToSelection(selectedColor, opacity);
    }, 150);
  });

  container.appendChild(opacityRow);
  return container;
}

function applyColorToSelection(color: string | null, opacity: number): void {
  let range = app.getViewport().getSelectedRange();
  if (!range) return;

  const code = app.getCode();

  let lineStart = range.from;
  while (lineStart > 0 && code[lineStart - 1] !== '\n') lineStart--;
  const beforeOnLine = code.substring(lineStart, range.from);
  const colorPrefixMatch = beforeOnLine.match(/color\s*\([^)]*\)\s*$/);
  if (colorPrefixMatch) {
    range = { from: range.from - colorPrefixMatch[0].length, to: range.to };
  }

  const selectedCode = code.substring(range.from, range.to);
  if (!selectedCode.trim()) return;

  const trimmedCode = selectedCode.trimStart() || selectedCode;

  const colorStmt = opacity < 1
    ? `color("${color}", alpha = ${opacity.toFixed(2)})`
    : `color("${color}")`;

  const isColorWrapped = /^color\s*\(/.test(trimmedCode);

  let newCode: string;
  if (color === null) {
    if (isColorWrapped) {
      let depth = 0;
      let colorEnd = 0;
      for (let i = 0; i < trimmedCode.length; i++) {
        if (trimmedCode[i] === '(') depth++;
        if (trimmedCode[i] === ')') { depth--; if (depth === 0) { colorEnd = i + 1; break; } }
      }
      while (colorEnd < trimmedCode.length && /\s/.test(trimmedCode[colorEnd])) colorEnd++;
      newCode = trimmedCode.substring(colorEnd);
    } else {
      return;
    }
  } else {
    if (isColorWrapped) {
      let depth = 0;
      let colorEnd = 0;
      for (let i = 0; i < trimmedCode.length; i++) {
        if (trimmedCode[i] === '(') depth++;
        if (trimmedCode[i] === ')') { depth--; if (depth === 0) { colorEnd = i + 1; break; } }
      }
      while (colorEnd < trimmedCode.length && /\s/.test(trimmedCode[colorEnd])) colorEnd++;
      const innerCode = trimmedCode.substring(colorEnd);
      newCode = `${colorStmt} ${innerCode}`;
    } else {
      newCode = `${colorStmt} ${trimmedCode}`;
    }
  }

  const updatedCode = code.substring(0, range.from) + newCode + code.substring(range.to);
  app.applyCodeChange(updatedCode);
  app.getViewport().selectBySourceOffset(range.from);
}

function showParamPanel(infos: ParamInfo[] | null): void {
  if (!infos || infos.length === 0) {
    paramPanelTitle.textContent = 'No selection';
    paramPanelFields.innerHTML = '';
    paramPanel.style.display = 'none';
    return;
  }

  paramPanelFields.innerHTML = '';
  paramPanel.style.display = 'flex';

  if (infos.length === 1) {
    paramPanelTitle.textContent = infos[0].moduleName;
    for (const param of infos[0].params) {
      paramPanelFields.appendChild(renderParamRow(param, infos[0].moduleName));
    }
  } else {
    paramPanelTitle.textContent = 'Properties';
    for (const info of infos) {
      if (info.params.length === 0) continue;
      const header = document.createElement('div');
      header.className = 'param-section-header';
      header.textContent = info.moduleName;
      paramPanelFields.appendChild(header);
      for (const param of info.params) {
        paramPanelFields.appendChild(renderParamRow(param, info.moduleName));
      }
    }
  }

  let currentColor: string | null = null;
  let currentOpacity = 1;
  for (const info of infos) {
    if (info.moduleName === 'color') {
      const cParam = info.params.find(p => p.name === 'c' || p.name === 'arg0') || info.params[0];
      const alphaParam = info.params.find(p => p.name === 'alpha' || p.name === 'arg1');
      if (cParam) currentColor = cParam.value.replace(/['"]/g, '');
      if (alphaParam) currentOpacity = parseFloat(alphaParam.value) || 1;
      break;
    }
  }
  paramPanelFields.appendChild(createColorPalette(currentColor, currentOpacity));

  // Inside a user module: edits change the module definition = every use
  const selKey = app.getViewport().getSelectedKey();
  const mod = selKey ? app.getEnclosingModule(selKey.offset) : null;
  if (mod) {
    const note = document.createElement('span');
    note.className = 'param-module-note';
    note.textContent = ` · in ${mod.name}()${mod.uses > 1 ? ` — used ${mod.uses}×` : ''}`;
    note.title = mod.uses > 1 ? `Changes here edit module ${mod.name}() and affect all ${mod.uses} uses` : `Defined inside module ${mod.name}()`;
    paramPanelTitle.appendChild(note);
  }
}

app.onParamChange(showParamPanel);

// --- Customizer (OpenSCAD-style variables panel) ---

const customizerRoot = document.getElementById('customizer-root');

function escapeHtml(s: string): string {
  return s.replace(/[&<>"']/g, c => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]!));
}

function writeField(field: CustomizerField, newSource: string): void {
  // Re-parse the current code so we use fresh offsets — the stored
  // `field.from`/`field.to` can go stale if another edit or a rapid
  // sequence of customizer changes triggered a re-render between the
  // UI change and the debounced write.
  const code = app.getCode();
  const fresh = parseCustomizerFields(code).find(f => f.name === field.name);
  if (!fresh) {
    console.warn(`Customizer: variable "${field.name}" no longer exists in source`);
    return;
  }
  const updated = code.substring(0, fresh.from) + newSource + code.substring(fresh.to);
  app.applyCodeChange(updated);
  // applyCodeChange suppresses onCodeUpdate, so manually keep the
  // customizer + outline in sync with the new code.
  renderCustomizer(updated);
  renderOutline();
}

function buildCustomizerRow(field: CustomizerField): HTMLElement {
  const row = document.createElement('div');
  row.className = 'cx-row';

  if (field.type === 'boolean') {
    row.classList.add('cx-bool');
    const label = document.createElement('span');
    label.className = 'cx-label';
    label.innerHTML = `${escapeHtml(field.label)} <span class="cx-name">(${escapeHtml(field.name)})</span>`;
    row.appendChild(label);
    const cb = document.createElement('input');
    cb.type = 'checkbox';
    cb.checked = field.value === 'true';
    cb.addEventListener('change', () => writeField(field, cb.checked ? 'true' : 'false'));
    row.appendChild(cb);
    return row;
  }

  const head = document.createElement('div');
  head.className = 'cx-head';
  const label = document.createElement('span');
  label.className = 'cx-label';
  label.innerHTML = `${escapeHtml(field.label)} <span class="cx-name">(${escapeHtml(field.name)})</span>`;
  head.appendChild(label);
  const val = document.createElement('span');
  val.className = 'cx-val';
  val.textContent = field.value;
  head.appendChild(val);
  row.appendChild(head);

  if (field.type === 'range') {
    const slider = document.createElement('input');
    slider.type = 'range';
    slider.min = String(field.min ?? 0);
    slider.max = String(field.max ?? 100);
    slider.step = String(field.step ?? 1);
    slider.value = field.value;
    let debounce: number | null = null;
    slider.addEventListener('input', () => {
      val.textContent = slider.value;
      if (debounce) clearTimeout(debounce);
      debounce = window.setTimeout(() => writeField(field, slider.value), 120);
    });
    row.appendChild(slider);
  } else if (field.type === 'list-number' || field.type === 'list-string') {
    const sel = document.createElement('select');
    for (const opt of field.options || []) {
      const o = document.createElement('option');
      const isString = field.type === 'list-string';
      o.value = isString ? `"${opt}"` : String(opt);
      o.textContent = String(opt);
      if (o.value === field.value || (isString && `"${opt}"` === field.value)) o.selected = true;
      sel.appendChild(o);
    }
    sel.addEventListener('change', () => {
      val.textContent = sel.value;
      writeField(field, sel.value);
    });
    row.appendChild(sel);
  } else if (field.type === 'list-labeled') {
    const sel = document.createElement('select');
    for (const opt of field.labeledOptions || []) {
      const o = document.createElement('option');
      o.value = opt.source;
      o.textContent = `${opt.label} (${opt.source})`;
      if (opt.source === field.value) o.selected = true;
      sel.appendChild(o);
    }
    sel.addEventListener('change', () => {
      val.textContent = sel.value;
      writeField(field, sel.value);
    });
    row.appendChild(sel);
  } else {
    // number, string, vector → text input (keep the source's quote/bracket style)
    const input = document.createElement('input');
    input.type = 'text';
    input.value = field.value;
    input.addEventListener('change', () => {
      val.textContent = input.value;
      writeField(field, input.value);
    });
    input.addEventListener('keydown', (e) => { if (e.key === 'Enter') input.blur(); });
    row.appendChild(input);
  }

  return row;
}

function renderCustomizer(code: string): void {
  if (!customizerRoot) return;
  const fields = parseCustomizerFields(code);
  customizerRoot.innerHTML = '';

  if (fields.length === 0) {
    customizerRoot.innerHTML = `
      <div class="sb-section">
        <div class="sb-head"><span>Parameters</span></div>
        <div class="sb-empty">No variables declared. Add top-level <code>name = value;</code> lines, optionally with <code>// [min:max]</code> or <code>// [opt1, opt2]</code> constraints.</div>
      </div>`;
    return;
  }

  // Group by 'group'
  const groups = new Map<string, CustomizerField[]>();
  for (const f of fields) {
    const key = f.group || '';
    if (!groups.has(key)) groups.set(key, []);
    groups.get(key)!.push(f);
  }

  for (const [groupName, groupFields] of groups) {
    const section = document.createElement('div');
    section.className = 'cx-group';
    if (groupName) {
      const head = document.createElement('div');
      head.className = 'cx-group-head';
      head.textContent = groupName;
      section.appendChild(head);
    } else {
      const head = document.createElement('div');
      head.className = 'cx-group-head';
      head.textContent = 'Parameters';
      section.appendChild(head);
    }
    for (const field of groupFields) {
      section.appendChild(buildCustomizerRow(field));
    }
    customizerRoot.appendChild(section);
  }
}

// Initial render (code-change re-renders are wired into the primary
// onCodeUpdate handler above — onCodeUpdate has a single-callback slot).
renderCustomizer(app.getCode());

// --- Outline panel ---

const outlineRoot = document.getElementById('outline-root')!;
// Declared here (above renderOutline) so the reapply-highlight call inside
// renderOutline doesn't hit the TDZ on first run.
let outlineSelectedOffset: number | null = null;
function reapplyOutlineSelection(): void {
  if (outlineSelectedOffset === null) return;
  const row = outlineRoot.querySelector<HTMLElement>(`.ol-row[data-from="${outlineSelectedOffset}"]`);
  if (row) row.classList.add('selected');
}
// Remember which sections are collapsed across re-renders
const collapsedSections = new Set<string>();
let latestErrors: ScadError[] = [];

function olSectionHeader(title: string, key: string, count?: number): HTMLElement {
  const section = document.createElement('div');
  section.className = 'ol-section' + (collapsedSections.has(key) ? ' collapsed' : '');
  section.dataset.sec = key;

  const head = document.createElement('div');
  head.className = 'ol-head';
  const titleSpan = document.createElement('span');
  titleSpan.textContent = title;
  head.appendChild(titleSpan);
  if (count !== undefined && count > 0) {
    const cnt = document.createElement('span');
    cnt.className = 'ol-count';
    cnt.textContent = String(count);
    head.appendChild(cnt);
  }
  const chev = document.createElement('span');
  chev.className = 'ol-chev';
  chev.textContent = '▾';
  head.appendChild(chev);

  head.addEventListener('click', () => {
    if (section.classList.toggle('collapsed')) collapsedSections.add(key);
    else collapsedSections.delete(key);
  });

  section.appendChild(head);
  const body = document.createElement('div');
  body.className = 'ol-body';
  section.appendChild(body);
  return section;
}

function olBody(section: HTMLElement): HTMLElement {
  return section.querySelector('.ol-body') as HTMLElement;
}

function olEmpty(msg: string): HTMLElement {
  const e = document.createElement('div');
  e.className = 'ol-empty';
  e.textContent = msg;
  return e;
}

// Render tree row — recursive with depth-based indent
const expandedTreeNodes = new Set<string>();

function buildTreeRow(node: RenderNode, depth: number, userModuleNames: Set<string>): HTMLElement[] {
  const out: HTMLElement[] = [];
  const hasChildren = node.children.length > 0;
  const nodeKey = `${node.from}-${node.to}`;
  // Default: expand everything so nested cuts/attaches are visible on first view.
  // User can collapse any subtree by clicking its chevron.
  if (!expandedTreeNodes.has(nodeKey) && hasChildren) {
    expandedTreeNodes.add(nodeKey);
  }
  const expanded = expandedTreeNodes.has(nodeKey);

  const row = document.createElement('div');
  row.className = 'ol-row' + (hasChildren ? ' has-children' : ' leaf') + (expanded ? ' expanded' : '');
  if (userModuleNames.has(node.name)) row.classList.add('ol-user');
  row.style.setProperty('--ol-depth', String(depth));
  row.dataset.from = String(node.from);

  const name = document.createElement('span');
  name.className = 'ol-name';
  name.textContent = node.name;
  row.appendChild(name);

  if (node.summary) {
    const sum = document.createElement('span');
    sum.className = 'ol-sum';
    sum.textContent = `(${node.summary})`;
    row.appendChild(sum);
  }

  const line = document.createElement('span');
  line.className = 'ol-line';
  line.textContent = `L${node.line}`;
  row.appendChild(line);

  row.addEventListener('click', (e) => {
    e.stopPropagation();
    // Expand/collapse on chevron; clicking elsewhere jumps + selects
    if (hasChildren && (e.target === row || e.target === name)) {
      // Toggle expansion
      if (expandedTreeNodes.has(nodeKey)) expandedTreeNodes.delete(nodeKey);
      else expandedTreeNodes.add(nodeKey);
      renderOutline();
      return;
    }
    // Jump to source + try to select in 3D
    app.goToLine(node.line, 1);
    const vp = app.getViewport();
    // Try exact-offset match first; if the AST node isn't its own ghost
    // (e.g., a cube nested inside a translate — the translate owns the
    // ghost), fall back to the innermost enclosing part/ghost.
    const exact = (vp as { selectBySourceOffset?: (n: number) => boolean }).selectBySourceOffset;
    const byCursor = (vp as { selectByCursorOffset?: (n: number) => boolean }).selectByCursorOffset;
    if (!(exact && exact.call(vp, node.from)) && byCursor) {
      byCursor.call(vp, node.from);
    }
  });
  out.push(row);

  if (hasChildren && expanded) {
    for (const child of node.children) {
      out.push(...buildTreeRow(child, depth + 1, userModuleNames));
    }
  }
  return out;
}

function renderOutline(): void {
  outlineRoot.innerHTML = '';

  const program = app.getAST();
  const code = app.getCode();
  const customizerFields = parseCustomizerFields(code);
  const data: OutlineData | null = program ? buildOutline(program) : null;

  // Section: Render tree
  {
    const count = data ? data.renderTree.length : 0;
    const sec = olSectionHeader('Render tree', 'render', count);
    const body = olBody(sec);
    body.classList.add('ol-tree');
    if (!data || data.renderTree.length === 0) {
      body.appendChild(olEmpty('No rendered statements.'));
    } else {
      const userNames = new Set((data.modules || []).map(m => m.name));
      for (const n of data.renderTree) {
        for (const row of buildTreeRow(n, 0, userNames)) body.appendChild(row);
      }
    }
    outlineRoot.appendChild(sec);
  }

  // Section: Symbols (modules / functions / variables)
  {
    const varFields = customizerFields;
    const modCount = data ? data.modules.length : 0;
    const fnCount = data ? data.functions.length : 0;
    const total = modCount + fnCount + varFields.length;
    const sec = olSectionHeader('Symbols', 'symbols', total);
    const body = olBody(sec);

    if (total === 0) {
      body.appendChild(olEmpty('No user modules, functions, or variables.'));
    } else {
      if (data && data.modules.length) {
        const h = document.createElement('div');
        h.className = 'ol-sub-head';
        h.textContent = `Modules (${data.modules.length})`;
        body.appendChild(h);
        for (const m of data.modules) {
          const row = document.createElement('div');
          row.className = 'ol-row leaf';
          row.dataset.from = String(m.from);
          row.innerHTML = `<span class="ol-name">${escapeHtml(m.name)}</span><span class="ol-sum">${escapeHtml(m.signature)}</span><span class="ol-line">L${m.line}</span>`;
          row.addEventListener('click', () => app.goToLine(m.line, 1));
          body.appendChild(row);
        }
      }
      if (data && data.functions.length) {
        const h = document.createElement('div');
        h.className = 'ol-sub-head';
        h.textContent = `Functions (${data.functions.length})`;
        body.appendChild(h);
        for (const f of data.functions) {
          const row = document.createElement('div');
          row.className = 'ol-row leaf';
          row.dataset.from = String(f.from);
          row.innerHTML = `<span class="ol-name">${escapeHtml(f.name)}</span><span class="ol-sum">${escapeHtml(f.signature)}</span><span class="ol-line">L${f.line}</span>`;
          row.addEventListener('click', () => app.goToLine(f.line, 1));
          body.appendChild(row);
        }
      }
      if (varFields.length) {
        const h = document.createElement('div');
        h.className = 'ol-sub-head';
        h.textContent = `Variables (${varFields.length})`;
        body.appendChild(h);
        for (const v of varFields) {
          // Count chars before `from` to get 1-based line
          const line = code.substring(0, v.from).split('\n').length;
          const row = document.createElement('div');
          row.className = 'ol-row leaf';
          row.innerHTML = `<span class="ol-name">${escapeHtml(v.name)}</span><span class="ol-sum">= ${escapeHtml(v.value)}</span><span class="ol-line">L${line}</span>`;
          row.addEventListener('click', () => app.goToLine(line, 1));
          body.appendChild(row);
        }
      }
    }
    outlineRoot.appendChild(sec);
  }

  // Section: Diagnostics
  {
    const errs = latestErrors.filter(e => e.severity !== 'warning');
    const warns = latestErrors.filter(e => e.severity === 'warning');
    const total = errs.length + warns.length;
    const sec = olSectionHeader(`Diagnostics${total ? ` (${errs.length} · ${warns.length})` : ''}`, 'diag', total);
    const body = olBody(sec);
    if (total === 0) {
      body.appendChild(olEmpty('No errors or warnings.'));
    } else {
      for (const e of errs) {
        const row = document.createElement('div');
        row.className = 'ol-row leaf diag-error';
        row.innerHTML = `<span class="ol-diag-icon">✕</span><span class="ol-sum">${escapeHtml(e.message)}</span><span class="ol-line">L${e.from.line}:${e.from.col}</span>`;
        row.addEventListener('click', () => app.goToLine(e.from.line, e.from.col));
        body.appendChild(row);
      }
      for (const w of warns) {
        const row = document.createElement('div');
        row.className = 'ol-row leaf diag-warn';
        row.innerHTML = `<span class="ol-diag-icon">⚠</span><span class="ol-sum">${escapeHtml(w.message)}</span><span class="ol-line">L${w.from.line}:${w.from.col}</span>`;
        row.addEventListener('click', () => app.goToLine(w.from.line, w.from.col));
        body.appendChild(row);
      }
    }
    outlineRoot.appendChild(sec);
  }

  // Section: Customizer tabs
  {
    const groups = new Map<string, number>();
    for (const f of customizerFields) {
      const g = f.group || '(ungrouped)';
      groups.set(g, (groups.get(g) || 0) + 1);
    }
    const sec = olSectionHeader('Customizer tabs', 'tabs', groups.size);
    const body = olBody(sec);
    if (groups.size === 0) {
      body.appendChild(olEmpty('No /* [Group] */ markers or variables.'));
    } else {
      for (const [groupName, count] of groups) {
        const row = document.createElement('div');
        row.className = 'ol-row leaf';
        row.innerHTML = `<span class="ol-name">${escapeHtml(groupName)}</span><span class="ol-line">${count}</span>`;
        row.addEventListener('click', () => {
          // Open params drawer, scroll the matching group into view
          openDrawer('params');
          requestAnimationFrame(() => {
            const heads = document.querySelectorAll('#customizer-root .cx-group-head');
            for (const h of Array.from(heads)) {
              if ((h.textContent || '').trim() === groupName) {
                (h as HTMLElement).scrollIntoView({ block: 'start', behavior: 'smooth' });
                break;
              }
            }
          });
        });
        body.appendChild(row);
      }
    }
    outlineRoot.appendChild(sec);
  }
  // Restore selection highlight after every re-render (code edits rebuild rows)
  if (typeof reapplyOutlineSelection === 'function') reapplyOutlineSelection();
}

renderOutline();

// Tab-switch pipeline-complete event — sync outline + customizer to new AST
window.addEventListener('scadplus:pipeline-complete', () => {
  renderCustomizer(app.getCode());
  renderOutline();
});

// --- Sync outline selection with viewport selection ---

function highlightOutlineRow(offset: number | null): void {
  outlineSelectedOffset = offset;
  outlineRoot.querySelectorAll<HTMLElement>('.ol-row').forEach(row => {
    row.classList.remove('selected');
  });
  if (offset === null) return;

  // If the target row isn't in the DOM yet, it's nested under a collapsed
  // tree node. Walk the outline to find the ancestor chain and expand each
  // one so the row shows up, then re-render and highlight.
  let row = outlineRoot.querySelector<HTMLElement>(`.ol-row[data-from="${offset}"]`);
  if (!row) {
    const program = app.getAST();
    if (program) {
      const data = buildOutline(program);
      const path: RenderNode[] = [];
      const findPath = (nodes: RenderNode[], chain: RenderNode[]): boolean => {
        for (const n of nodes) {
          const next = [...chain, n];
          if (n.from === offset) { path.push(...next); return true; }
          if (n.children.length && findPath(n.children, next)) return true;
        }
        return false;
      };
      findPath(data.renderTree, []);
      if (path.length > 0) {
        // Expand every ancestor (all nodes on the path except the target row itself)
        for (let i = 0; i < path.length - 1; i++) {
          const key = `${path[i].from}-${path[i].to}`;
          expandedTreeNodes.add(key);
        }
        renderOutline();
        row = outlineRoot.querySelector<HTMLElement>(`.ol-row[data-from="${offset}"]`);
      }
    }
  }
  if (row) {
    row.classList.add('selected');
    row.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
  }
}
app.onSelectionChange((from) => {
  highlightOutlineRow(from ? from.offset : null);
});

const selOrgChip = document.getElementById('sel-org-chip') as HTMLElement | null;

app.onTransformUpdate((info: TransformInfo | null) => {
  if (!info) { selStatus.style.display = 'none'; return; }
  selStatus.style.display = 'flex';
  const fmt = (v: number) => Math.abs(v) < 0.005 ? '0' : v.toFixed(2);
  if (selOrgChip) selOrgChip.hidden = !info.relativeToOrigin;
  selPosX.textContent = `X ${fmt(info.position[0])}`;
  selPosY.textContent = `Y ${fmt(info.position[1])}`;
  selPosZ.textContent = `Z ${fmt(info.position[2])}`;
  if (info.rotation) {
    selRotX.textContent = `X ${fmt(info.rotation[0])}°`;
    selRotY.textContent = `Y ${fmt(info.rotation[1])}°`;
    selRotZ.textContent = `Z ${fmt(info.rotation[2])}°`;
  } else {
    selRotX.textContent = 'X 0°';
    selRotY.textContent = 'Y 0°';
    selRotZ.textContent = 'Z 0°';
  }
  selSizeX.textContent = `X ${fmt(info.size[0])}`;
  selSizeY.textContent = `Y ${fmt(info.size[1])}`;
  selSizeZ.textContent = `Z ${fmt(info.size[2])}`;
});

// --- Model metrics: status-bar bounds + volume/area/mass popover ---
{
  const fmtLen = (v: number) => (Math.abs(v) < 0.005 ? '0' : v.toFixed(2));
  const fmtSize = (b: ScadBounds) =>
    `${fmtLen(b.max[0] - b.min[0])} × ${fmtLen(b.max[1] - b.min[1])} × ${fmtLen(b.max[2] - b.min[2])} mm`;
  // mm³ → cm³ (÷1000); mass in g from density g/cm³
  const fmtVol = (mm3: number) => `${(mm3 / 1000).toFixed(2)} cm³`;
  const fmtArea = (mm2: number) => `${(mm2 / 100).toFixed(2)} cm²`;
  const fmtMass = (mm3: number, density: number) => {
    const g = (mm3 / 1000) * density;
    return g >= 1000 ? `${(g / 1000).toFixed(3)} kg` : `${g.toFixed(1)} g`;
  };

  const MATERIAL_KEY = 'scadplus-material-density';
  const btn = document.getElementById('stat-bounds-btn') as HTMLElement;
  const pop = document.getElementById('metricsPop') as HTMLElement;
  const material = document.getElementById('mx-material') as HTMLSelectElement;
  const el = (id: string) => document.getElementById(id) as HTMLElement;
  try {
    const saved = localStorage.getItem(MATERIAL_KEY);
    if (saved && [...material.options].some(o => o.value === saved)) material.value = saved;
  } catch { /* storage unavailable */ }

  const fillPop = () => {
    const density = parseFloat(material.value) || 1;
    const m = app.computeModelMetrics();
    el('mx-size').textContent = m ? fmtSize(m.bounds) : '–';
    el('mx-volume').textContent = m ? fmtVol(m.volume) : '–';
    el('mx-area').textContent = m ? fmtArea(m.area) : '–';
    el('mx-mass').textContent = m ? fmtMass(m.volume, density) : '–';
    const range = app.getViewport().getSelectedRange();
    const sm = range ? app.computeRangeMetrics(range.from, range.to, app.getViewport().getSelectedKey()?.instance) : null;
    el('mx-sel').hidden = !sm;
    if (sm) {
      el('mx-sel-size').textContent = fmtSize(sm.bounds);
      el('mx-sel-volume').textContent = fmtVol(sm.volume);
      el('mx-sel-mass').textContent = fmtMass(sm.volume, density);
    }
  };
  const closePop = () => pop.setAttribute('hidden', '');
  const openPop = () => {
    fillPop();
    const r = btn.getBoundingClientRect();
    pop.style.left = `${Math.round(Math.max(8, Math.min(r.left, window.innerWidth - 288)))}px`;
    pop.style.top = '';
    pop.style.bottom = `${Math.round(window.innerHeight - r.top + 6)}px`;
    pop.removeAttribute('hidden');
  };
  btn.addEventListener('click', () => (pop.hasAttribute('hidden') ? openPop() : closePop()));
  el('metricsPopClose').addEventListener('click', closePop);
  material.addEventListener('change', () => {
    try { localStorage.setItem(MATERIAL_KEY, material.value); } catch { /* storage unavailable */ }
    fillPop();
  });
  document.addEventListener('mousedown', (e) => {
    if (pop.hasAttribute('hidden')) return;
    const t = e.target as Node;
    if (pop.contains(t) || btn.contains(t)) return;
    closePop();
  });
  document.addEventListener('keydown', (e) => {
    if (e.key === 'Escape' && !pop.hasAttribute('hidden')) closePop();
  });

  window.addEventListener('scadplus:render-complete', () => {
    const b = app.getModelBounds();
    statBounds.textContent = b ? fmtSize(b) : '–';
    if (!pop.hasAttribute('hidden')) fillPop();
  });
  app.onSelectionChange(() => {
    if (!pop.hasAttribute('hidden')) fillPop();
  });
}

paramPanelClose.addEventListener('click', () => {
  paramPanelTitle.textContent = 'No selection';
  paramPanelFields.innerHTML = '';
  paramPanel.style.display = 'none';
  app.getViewport().deselectAll();
});

// --- User Origin toolbar + popover ---
{
  const btnSet = document.getElementById('origin-set') as HTMLButtonElement | null;
  const btnReset = document.getElementById('origin-reset') as HTMLButtonElement | null;
  const btnEdit = document.getElementById('origin-edit') as HTMLButtonElement | null;
  const pop = document.getElementById('originPop') as HTMLElement | null;
  const popClose = document.getElementById('originPopClose') as HTMLElement | null;
  const inX = document.getElementById('origin-in-x') as HTMLInputElement | null;
  const inY = document.getElementById('origin-in-y') as HTMLInputElement | null;
  const inZ = document.getElementById('origin-in-z') as HTMLInputElement | null;
  const btnFromSel = document.getElementById('origin-pop-from-sel') as HTMLButtonElement | null;
  const btnDone = document.getElementById('origin-pop-done') as HTMLButtonElement | null;
  const btnDims = document.getElementById('origin-dims') as HTMLButtonElement | null;

  const vp = () => app.getViewport();

  // Reflect initial toggle state
  if (btnDims) btnDims.classList.toggle('active', app.getShowOriginDims());
  btnDims?.addEventListener('click', () => {
    const next = !app.getShowOriginDims();
    app.setShowOriginDims(next);
    btnDims.classList.toggle('active', next);
  });

  const closePop = () => { if (pop) pop.setAttribute('hidden', ''); };
  const openPop = () => {
    if (!pop || !btnEdit) return;
    const [x, y, z] = vp().getUserOriginScad();
    if (inX) inX.value = String(+x.toFixed(3));
    if (inY) inY.value = String(+y.toFixed(3));
    if (inZ) inZ.value = String(+z.toFixed(3));
    const r = btnEdit.getBoundingClientRect();
    pop.style.top = `${Math.round(r.bottom + 6)}px`;
    pop.style.left = `${Math.round(Math.max(8, r.right - 240))}px`;
    pop.removeAttribute('hidden');
    inX?.focus();
    inX?.select();
  };

  const applyFromInputs = () => {
    const x = parseFloat(inX?.value || '0') || 0;
    const y = parseFloat(inY?.value || '0') || 0;
    const z = parseFloat(inZ?.value || '0') || 0;
    vp().setUserOriginScad(x, y, z);
  };

  btnSet?.addEventListener('click', () => {
    if (!vp().setUserOriginToSelection()) {
      btnSet.animate(
        [{ outlineColor: 'var(--accent)' }, { outlineColor: 'transparent' }],
        { duration: 400 }
      );
    }
  });
  btnReset?.addEventListener('click', () => vp().resetUserOrigin());
  btnEdit?.addEventListener('click', (e) => {
    e.stopPropagation();
    if (!pop) return;
    if (pop.hasAttribute('hidden')) openPop();
    else closePop();
  });
  popClose?.addEventListener('click', closePop);
  btnDone?.addEventListener('click', () => { applyFromInputs(); closePop(); });
  btnFromSel?.addEventListener('click', () => {
    if (vp().setUserOriginToSelection()) {
      const [x, y, z] = vp().getUserOriginScad();
      if (inX) inX.value = String(+x.toFixed(3));
      if (inY) inY.value = String(+y.toFixed(3));
      if (inZ) inZ.value = String(+z.toFixed(3));
    }
  });
  [inX, inY, inZ].forEach((el) => {
    el?.addEventListener('change', applyFromInputs);
    el?.addEventListener('keydown', (ev) => {
      if ((ev as KeyboardEvent).key === 'Enter') { applyFromInputs(); closePop(); }
    });
  });

  // Click-outside + Escape to close
  document.addEventListener('mousedown', (e) => {
    if (!pop || pop.hasAttribute('hidden')) return;
    const t = e.target as Node;
    if (pop.contains(t) || (btnEdit && btnEdit.contains(t))) return;
    closePop();
  });
  document.addEventListener('keydown', (e) => {
    if (e.key === 'Escape' && pop && !pop.hasAttribute('hidden')) closePop();
  });

  // Apply the active project's origin on startup
  const startProj = getActiveProject();
  if (startProj.origin) {
    vp().setUserOriginScad(startProj.origin[0], startProj.origin[1], startProj.origin[2]);
  } else {
    vp().resetUserOrigin();
  }

  // Persist origin to the active project on every change
  vp().onOriginChange(() => {
    const [x, y, z] = vp().getUserOriginScad();
    const proj = getActiveProject();
    if (Math.abs(x) + Math.abs(y) + Math.abs(z) > 1e-6) {
      proj.origin = [x, y, z];
    } else {
      delete proj.origin;
    }
    saveProjects();
  });
}

// --- Keyboard shortcuts ---

window.addEventListener('keydown', (e) => {
  const active = document.activeElement;
  if (active && active.closest('.cm-editor')) return;
  if (active && (active.tagName === 'INPUT' || active.tagName === 'TEXTAREA')) return;
  if (active && (active as HTMLElement).isContentEditable) return;

  if (viewport.isDimensionDragging()) {
    const key = e.key.toLowerCase();
    if (key === 'x' || key === 'y' || key === 'z') {
      const worldAxis: 'x' | 'y' | 'z' = key === 'x' ? 'x' : key === 'y' ? 'z' : 'y';
      viewport.setDragAxisConstraint(worldAxis);
      kbdConstrainedScadAxis = kbdConstrainedScadAxis === key ? null : key;
      updateAxisConstraintUI();
    }
    return;
  }

  if (e.code === 'Numpad1') { e.preventDefault(); viewport.setStandardView(e.ctrlKey ? 'back' : 'front'); return; }
  if (e.code === 'Numpad3') { e.preventDefault(); viewport.setStandardView(e.ctrlKey ? 'left' : 'right'); return; }
  if (e.code === 'Numpad7') { e.preventDefault(); viewport.setStandardView(e.ctrlKey ? 'bottom' : 'top'); return; }
  if (e.code === 'Numpad5') { e.preventDefault(); syncProjectionUI(viewport.toggleProjection()); return; }

  if (e.key === 'Escape' && !inspectMenu.hasAttribute('hidden')) { closeInspectMenu(); return; }
  if (e.key === 'Escape' && viewport.isPicking()) { viewport.cancelPickOnce(); snapPop.classList.remove('picking'); refreshSnap(); return; }
  if (e.key === 'Escape' && !snapPop.hasAttribute('hidden')) { closeSnap(); return; }
  const measure = viewport.getMeasureTool();
  if (e.key === 'Escape' && measure.isActive()) {
    // First Esc drops the half-done measurement, second leaves the tool
    if (!measure.cancelPending()) setMeasureMode(null);
    return;
  }

  if (e.key === 'Escape') {
    if (aboutOverlay.classList.contains('open')) { closeAbout(); return; }
    if (helpOverlay.classList.contains('open')) { closeHelp(); return; }
    if (currentDrawer) { closeDrawer(); return; }
  }

  switch (e.key) {
    case '?': openHelp(); return;
  }

  switch (e.key.toLowerCase()) {
    case 't': viewport.setTransformMode('translate'); break;
    case 'r': viewport.setTransformMode('rotate'); break;
    case 's': viewport.setTransformMode('scale'); break;
    case 'f': viewport.fitToSelection(); break;
    case 'n': viewport.fitToSelectionNormal(); break;
    case 'm': setMeasureMode(measure.getMode() ? null : 'distance'); break;
    case 'g': if (snapPop.hasAttribute('hidden')) openSnap(); else closeSnap(); break;
    case 'escape': viewport.deselectAll(); break;
    case 'delete':
    case 'backspace':
      e.preventDefault();
      app.deleteSelection();
      break;
  }
});

// --- Measure tool (toolbar group + status prompt) ---

const inspectBtn = document.getElementById('inspect-btn') as HTMLButtonElement;
const inspectMenu = document.getElementById('inspectMenu') as HTMLElement;
const measureButtons = [...inspectMenu.querySelectorAll<HTMLButtonElement>('[data-measure]')];
const measureStatus = document.getElementById('measure-status') as HTMLElement;
const MEASURE_SHORT: Record<MeasureMode, string> = { distance: 'Dist', angle: 'Angle', radius: 'Radius' };
/** Chip reflects the active inspect tool (measure mode and/or section view). */
function syncInspectChip(): void {
  const mode = viewport.getMeasureTool().getMode();
  const sectionOn = app.getSection() !== null;
  const parts = [mode ? MEASURE_SHORT[mode] : null, sectionOn ? 'Section' : null].filter(Boolean);
  inspectBtn.querySelector('.insp-label')!.textContent = parts.length ? parts.join(' + ') : 'Inspect';
  inspectBtn.classList.toggle('active', parts.length > 0);
}
const closeInspectMenu = () => inspectMenu.setAttribute('hidden', '');
inspectBtn.addEventListener('click', () => {
  if (!inspectMenu.hasAttribute('hidden')) { closeInspectMenu(); return; }
  const r = inspectBtn.getBoundingClientRect();
  inspectMenu.style.top = `${Math.round(r.bottom + 4)}px`;
  inspectMenu.style.left = `${Math.round(Math.max(8, Math.min(r.left, window.innerWidth - 228)))}px`;
  inspectMenu.removeAttribute('hidden');
});
document.addEventListener('mousedown', (e) => {
  if (inspectMenu.hasAttribute('hidden')) return;
  const t = e.target as Node;
  if (inspectMenu.contains(t) || inspectBtn.contains(t)) return;
  closeInspectMenu();
});
viewport.getMeasureTool().onStatus((text) => {
  measureStatus.hidden = !text;
  measureStatus.textContent = text ?? '';
});
function setMeasureMode(mode: MeasureMode | null): void {
  viewport.setMeasureMode(mode);
  for (const b of measureButtons) b.classList.toggle('active', b.dataset.measure === mode);
  syncInspectChip();
}
for (const b of measureButtons) {
  b.addEventListener('click', () => {
    const mode = b.dataset.measure as MeasureMode;
    setMeasureMode(viewport.getMeasureTool().getMode() === mode ? null : mode);
    closeInspectMenu();
  });
}

// --- Section view (toolbar chip + bottom-left panel) ---
{
  const toggle = document.getElementById('section-toggle') as HTMLButtonElement;
  const panel = document.getElementById('section-panel') as HTMLElement;
  const slider = document.getElementById('section-slider') as HTMLInputElement;
  const valueIn = document.getElementById('section-value') as HTMLInputElement;
  const flipBtn = document.getElementById('section-flip') as HTMLButtonElement;
  const areaEl = document.getElementById('section-area') as HTMLElement;
  const axisBtns = [...panel.querySelectorAll<HTMLButtonElement>('[data-section-axis]')];
  const AXIS_INDEX = { x: 0, y: 1, z: 2 } as const;
  let state: SectionState = { axis: 'z', offset: 0, flip: false };

  const axisRange = (): [number, number] => {
    const b = app.getModelBounds();
    if (!b) return [-10, 10];
    const k = AXIS_INDEX[state.axis];
    return [b.min[k], b.max[k]];
  };
  const syncUI = () => {
    const [lo, hi] = axisRange();
    slider.min = String(lo);
    slider.max = String(hi);
    slider.step = String(Math.max((hi - lo) / 1000, 0.001));
    slider.value = String(state.offset);
    valueIn.value = String(Math.round(state.offset * 100) / 100);
    for (const b of axisBtns) b.classList.toggle('active', b.dataset.sectionAxis === state.axis);
    flipBtn.classList.toggle('active', state.flip);
    areaEl.textContent = `${app.getSectionArea().toFixed(2)} mm²`;
  };
  const apply = () => { app.setSection(state); syncUI(); };
  const open = () => {
    const [lo, hi] = axisRange();
    state.offset = (lo + hi) / 2;
    panel.hidden = false;
    toggle.classList.add('active');
    apply();
    syncInspectChip();
  };
  const close = () => {
    panel.hidden = true;
    toggle.classList.remove('active');
    app.setSection(null);
    syncInspectChip();
  };

  toggle.addEventListener('click', () => { (panel.hidden ? open() : close()); closeInspectMenu(); });
  document.getElementById('section-close')!.addEventListener('click', close);
  for (const b of axisBtns) {
    b.addEventListener('click', () => {
      state.axis = b.dataset.sectionAxis as SectionAxis;
      const [lo, hi] = axisRange();
      state.offset = (lo + hi) / 2;
      apply();
    });
  }
  slider.addEventListener('input', () => { state.offset = parseFloat(slider.value); apply(); });
  valueIn.addEventListener('change', () => {
    const v = parseFloat(valueIn.value);
    if (Number.isFinite(v)) { state.offset = v; apply(); }
  });
  flipBtn.addEventListener('click', () => { state.flip = !state.flip; apply(); });
  // Model changed → keep the cut position, refresh slider range and area
  window.addEventListener('scadplus:render-complete', () => { if (!panel.hidden) syncUI(); });
}

// --- Snap selection (selection HUD button / G) ---

type SnapPlane = 'xy' | 'xz' | 'yz';
type SnapTargetKind = 'origin' | 'parent' | 'part' | 'point';
const PLANE_AXES: Record<SnapPlane, { col: number; row: number; normal: number }> = {
  xy: { col: 0, row: 1, normal: 2 },
  xz: { col: 0, row: 2, normal: 1 },
  yz: { col: 1, row: 2, normal: 0 },
};
const AXIS_SIDES = [['left', 'right'], ['front', 'back'], ['bottom', 'top']];
const SNAP_AXIS_NAMES = ['X', 'Y', 'Z'];

/** 3×3 grid (in-plane anchor) + select for the plane's normal axis. */
function renderAnchorWidget(host: HTMLElement, plane: SnapPlane, anchor: SnapAnchor, onChange: (a: SnapAnchor) => void): void {
  const { col, row, normal } = PLANE_AXES[plane];
  host.innerHTML = '';
  const grid = document.createElement('div');
  grid.className = 'snap-grid';
  for (let r = 0; r < 3; r++) {
    for (let c = 0; c < 3; c++) {
      const cv = (c - 1) as AxisAnchor, rv = (1 - r) as AxisAnchor;
      const b = document.createElement('button');
      b.type = 'button';
      b.textContent = r === 1 && c === 1 ? '●' : '·';
      b.title = `${SNAP_AXIS_NAMES[col]} ${['min', 'center', 'max'][c]}, ${SNAP_AXIS_NAMES[row]} ${['max', 'center', 'min'][r]}`;
      b.classList.toggle('active', anchor[col] === cv && anchor[row] === rv);
      b.addEventListener('click', () => {
        const next = [...anchor] as SnapAnchor;
        next[col] = cv;
        next[row] = rv;
        onChange(next);
      });
      grid.appendChild(b);
    }
  }
  const axes = document.createElement('div');
  axes.className = 'snap-axes';
  const label = document.createElement('span');
  label.textContent = `${SNAP_AXIS_NAMES[normal]} axis`;
  const sel = document.createElement('select');
  const [lo, hi] = AXIS_SIDES[normal];
  for (const [v, text] of [['keep', 'keep'], ['-1', `${lo} (min)`], ['0', 'center'], ['1', `${hi} (max)`]]) {
    const o = document.createElement('option');
    o.value = v;
    o.textContent = text;
    sel.appendChild(o);
  }
  sel.value = anchor[normal] === null ? 'keep' : String(anchor[normal]);
  sel.addEventListener('change', () => {
    const next = [...anchor] as SnapAnchor;
    next[normal] = sel.value === 'keep' ? null : (Number(sel.value) as AxisAnchor);
    onChange(next);
  });
  axes.append(label, sel);
  host.append(grid, axes);
}

const snapPop = document.getElementById('snapPop') as HTMLElement;
const snapBtn = document.getElementById('sel-snap-btn') as HTMLButtonElement;
const snapSrcHost = document.getElementById('snap-src') as HTMLElement;
const snapTgtHost = document.getElementById('snap-tgt') as HTMLElement;
const snapInfo = document.getElementById('snap-info') as HTMLElement;
const snapMsg = document.getElementById('snap-msg') as HTMLElement;
const snapApply = document.getElementById('snap-apply') as HTMLButtonElement;
const snapState = {
  plane: 'xy' as SnapPlane,
  src: [0, 0, null] as SnapAnchor,
  tgt: [0, 0, null] as SnapAnchor,
  target: 'origin' as SnapTargetKind,
  pickedBox: null as Box3 | null,
  pickedPoint: null as V3 | null,
  delta: null as V3 | null,
};

function setSnapMessage(text: string | null): void {
  snapMsg.hidden = !text;
  snapMsg.textContent = text ?? '';
}

/** Recompute the move for the current options, update preview + info. */
function refreshSnap(): void {
  renderAnchorWidget(snapSrcHost, snapState.plane, snapState.src, (a) => { snapState.src = a; refreshSnap(); });
  const boxTarget = snapState.target === 'parent' || snapState.target === 'part';
  snapTgtHost.hidden = !boxTarget;
  if (boxTarget) renderAnchorWidget(snapTgtHost, snapState.plane, snapState.tgt, (a) => { snapState.tgt = a; refreshSnap(); });
  for (const b of snapPop.querySelectorAll<HTMLButtonElement>('[data-plane]')) b.classList.toggle('active', b.dataset.plane === snapState.plane);
  const o = viewport.getUserOriginScad();
  const hasOrg = o.some(v => Math.abs(v) > 1e-6);
  (document.getElementById('snap-origin-label') as HTMLElement).textContent =
    hasOrg ? `ORG (${o.map(v => +v.toFixed(2)).join(', ')})` : 'Origin (0,0,0)';
  (snapPop.querySelector('input[value="parent"]') as HTMLInputElement).disabled = !viewport.getParentScadBounds();

  snapState.delta = null;
  snapApply.disabled = true;
  viewport.showSnapPreview(null);
  if (viewport.isPicking()) return;
  const source = viewport.getSelectedScadBounds();
  const range = viewport.getSelectedRange();
  if (!source || !range) { snapInfo.textContent = 'Select a part first'; setSnapMessage(null); return; }

  let target: SnapTarget | null = null;
  switch (snapState.target) {
    case 'origin': target = { kind: 'point', point: o }; break;
    case 'parent': {
      const pb = viewport.getParentScadBounds();
      target = pb ? { kind: 'box', box: pb, anchor: snapState.tgt } : null;
      break;
    }
    case 'part': target = snapState.pickedBox ? { kind: 'box', box: snapState.pickedBox, anchor: snapState.tgt } : null; break;
    case 'point': target = snapState.pickedPoint ? { kind: 'point', point: snapState.pickedPoint } : null; break;
  }
  if (!target) {
    snapInfo.textContent = snapState.target === 'parent' ? 'The selection has no parent part' : 'Pick a target in the viewport';
    setSnapMessage(null);
    return;
  }
  const d = snapDelta(source, snapState.src, target);
  const fmt = (v: number) => (Math.abs(v) < 0.0005 ? '0' : v.toFixed(3).replace(/\.?0+$/, ''));
  if (d.every(v => Math.abs(v) < 0.001)) {
    snapInfo.textContent = 'Already in place';
    setSnapMessage(null);
    return;
  }
  snapInfo.textContent = `Move ΔX ${fmt(d[0])}  ΔY ${fmt(d[1])}  ΔZ ${fmt(d[2])}`;
  const check = app.resolveLocalMove(range.from, range.to, d);
  if (!check.ok) { setSnapMessage(`Can't snap: ${check.reason}.`); return; }
  setSnapMessage(null);
  snapState.delta = d;
  snapApply.disabled = false;
  viewport.showSnapPreview({
    min: [source.min[0] + d[0], source.min[1] + d[1], source.min[2] + d[2]],
    max: [source.max[0] + d[0], source.max[1] + d[1], source.max[2] + d[2]],
  });
}

function openSnap(): void {
  if (!viewport.getSelectedRange()) return;
  if (viewport.getMeasureTool().isActive()) setMeasureMode(null);
  snapState.pickedBox = null;
  snapState.pickedPoint = null;
  const r = snapBtn.getBoundingClientRect();
  snapPop.style.top = `${Math.round(r.bottom + 6)}px`;
  snapPop.style.left = `${Math.round(Math.max(8, Math.min(r.right - 270, window.innerWidth - 278)))}px`;
  snapPop.removeAttribute('hidden');
  refreshSnap();
}

function closeSnap(): void {
  snapPop.setAttribute('hidden', '');
  snapPop.classList.remove('picking');
  viewport.cancelPickOnce();
  viewport.showSnapPreview(null);
}

function startSnapPick(kind: 'part' | 'point'): void {
  snapState.target = kind;
  (snapPop.querySelector(`input[value="${kind}"]`) as HTMLInputElement).checked = true;
  viewport.showSnapPreview(null);
  snapApply.disabled = true;
  snapInfo.textContent = kind === 'part' ? 'Click a part in the viewport… (Esc cancels)' : 'Click a face, edge or vertex… (Esc cancels)';
  setSnapMessage(null);
  snapPop.classList.add('picking');
  viewport.pickOnce(kind, (res) => {
    snapPop.classList.remove('picking');
    if (kind === 'part') snapState.pickedBox = res?.bounds ?? null;
    else snapState.pickedPoint = res?.point ?? null;
    refreshSnap();
  });
}

snapBtn.addEventListener('click', () => (snapPop.hasAttribute('hidden') ? openSnap() : closeSnap()));
document.getElementById('snapPopClose')!.addEventListener('click', closeSnap);
// Esc inside the popover (focus on a radio/select is ignored by the global handler)
snapPop.addEventListener('keydown', (e) => {
  if (e.key === 'Escape') { e.stopPropagation(); closeSnap(); }
});
document.getElementById('snap-cancel')!.addEventListener('click', closeSnap);
for (const b of snapPop.querySelectorAll<HTMLButtonElement>('[data-plane]')) {
  b.addEventListener('click', () => {
    snapState.plane = b.dataset.plane as SnapPlane;
    const { col, row } = PLANE_AXES[snapState.plane];
    const fresh: SnapAnchor = [null, null, null];
    fresh[col] = 0;
    fresh[row] = 0;
    snapState.src = [...fresh] as SnapAnchor;
    snapState.tgt = [...fresh] as SnapAnchor;
    refreshSnap();
  });
}
for (const input of snapPop.querySelectorAll<HTMLInputElement>('input[name="snap-target"]')) {
  input.addEventListener('change', () => {
    snapState.target = input.value as SnapTargetKind;
    if (snapState.target === 'part' && !snapState.pickedBox) { startSnapPick('part'); return; }
    if (snapState.target === 'point' && !snapState.pickedPoint) { startSnapPick('point'); return; }
    viewport.cancelPickOnce();
    refreshSnap();
  });
}
document.getElementById('snap-pick-part')!.addEventListener('click', (e) => { e.preventDefault(); startSnapPick('part'); });
document.getElementById('snap-pick-point')!.addEventListener('click', (e) => { e.preventDefault(); startSnapPick('point'); });
snapApply.addEventListener('click', () => {
  const range = viewport.getSelectedRange();
  if (!range || !snapState.delta) return;
  const res = app.moveRangeWorld(range.from, range.to, snapState.delta);
  if (!res.ok) { setSnapMessage(`Can't snap: ${res.reason}.`); return; }
  closeSnap();
});
// Selection changed/cleared while open → follow it (or close)
app.onSelectionChange((from) => {
  if (snapPop.hasAttribute('hidden') || viewport.isPicking()) return;
  if (!from) closeSnap();
  else refreshSnap();
});

// --- Wireframe / Solid ---

wireframeBtn.addEventListener('click', () => {
  const isWire = app.toggleWireframe();
  wireframeBtn.classList.toggle('active', isWire);
  if (solidBtn) solidBtn.classList.toggle('active', !isWire);
});
if (solidBtn) {
  solidBtn.addEventListener('click', () => {
    // Force solid mode
    if (wireframeBtn.classList.contains('active')) {
      const isWire = app.toggleWireframe();
      wireframeBtn.classList.toggle('active', isWire);
      solidBtn.classList.toggle('active', !isWire);
    }
  });
}

// --- Render + Auto (placeholder wiring — Render triggers re-render) ---

// --- Load / Save / Import / Export ---

const menuLoadBtn = document.getElementById('menu-load')!;
const menuSaveBtn = document.getElementById('menu-save')!;
const menuImportBtn = document.getElementById('menu-import')!;
const menuStepToScadBtn = document.getElementById('menu-step-to-scad')!;
const menuExportStlBtn = document.getElementById('menu-export-stl')!;
const menuExport3mfBtn = document.getElementById('menu-export-3mf')!;

const fileInput = document.createElement('input');
fileInput.type = 'file';
fileInput.accept = '.scad';
fileInput.style.display = 'none';
document.body.appendChild(fileInput);

const stepFileInput = document.createElement('input');
stepFileInput.type = 'file';
stepFileInput.accept = '.step,.stp,.stl';
stepFileInput.style.display = 'none';
document.body.appendChild(stepFileInput);

menuLoadBtn.addEventListener('click', () => fileInput.click());

fileInput.addEventListener('change', () => {
  const file = fileInput.files?.[0];
  if (!file) return;
  file.text().then((code) => app.loadCode(code));
  fileInput.value = '';
});

menuImportBtn.addEventListener('click', () => stepFileInput.click());

stepFileInput.addEventListener('change', () => {
  const file = stepFileInput.files?.[0];
  if (!file) return;
  const filename = file.name;
  const ext = filename.split('.').pop()?.toLowerCase() ?? '';
  menuImportBtn.textContent = 'Importing...';
  file.arrayBuffer().then((buffer) => {
    if (ext === 'stl') return app.importSTL(filename, buffer);
    return app.importSTEP(filename, new Uint8Array(buffer));
  }).then(() => {
    const code = app.getCode().trim();
    if (!code || !code.includes(`import("${filename}")`)) {
      const importLine = `import("${filename}");\n`;
      const newCode = code ? importLine + code : importLine;
      app.loadCode(newCode);
    }
    window.umami?.track('import_mesh', { filename });
  }).catch((err) => {
    console.error('Mesh import failed:', err);
    alert(`Failed to import file: ${(err as Error).message}`);
  }).finally(() => {
    menuImportBtn.textContent = 'Import Mesh';
    stepFileInput.value = '';
  });
});

const scadFileInput = document.createElement('input');
scadFileInput.type = 'file';
scadFileInput.accept = '.step,.stp';
scadFileInput.style.display = 'none';
document.body.appendChild(scadFileInput);

menuStepToScadBtn.addEventListener('click', () => scadFileInput.click());

scadFileInput.addEventListener('change', () => {
  const file = scadFileInput.files?.[0];
  if (!file) return;
  menuStepToScadBtn.textContent = 'Converting...';
  file.arrayBuffer().then((buffer) => app.importSTEPtoScad(new Uint8Array(buffer)))
    .then((scad) => app.loadCode(scad))
    .catch((err) => {
      console.error('STEP→SCAD conversion failed:', err);
      alert(`Failed to convert STEP file: ${(err as Error).message}`);
    })
    .finally(() => {
      menuStepToScadBtn.textContent = 'STEP → SCAD';
      scadFileInput.value = '';
    });
});

menuSaveBtn.addEventListener('click', () => {
  const code = app.getCode();
  const blob = new Blob([code], { type: 'text/plain' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `${getActiveProject().name}.scad`;
  a.click();
  URL.revokeObjectURL(url);
});

function downloadBlob(data: ArrayBuffer | Uint8Array | string, filename: string, mime: string): void {
  const blob = new Blob([data instanceof Uint8Array ? data.slice().buffer : data], { type: mime });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
}

function exportFilename(ext: string): string {
  const raw = (getActiveProject().name || '').trim();
  const safe = raw.replace(/[\/\\:*?"<>|]+/g, '_').replace(/\s+/g, '_');
  return (safe || 'model') + '.' + ext;
}

menuExportStlBtn.addEventListener('click', () => {
  const data = app.getViewport().exportSTL();
  if (!data) return;
  downloadBlob(new Uint8Array(data.buffer as ArrayBuffer, data.byteOffset, data.byteLength), exportFilename('stl'), 'application/octet-stream');
  window.umami?.track('export_stl');
});

menuExport3mfBtn.addEventListener('click', () => {
  const data = app.getViewport().export3MF();
  if (!data) return;
  downloadBlob(data, exportFilename('3mf'), 'application/vnd.ms-package.3dmanufacturing-3dmodel+xml');
  window.umami?.track('export_3mf');
});

// --- Share ---

function doShare(mode: 'link' | 'preview' | 'embed'): void {
  let url: string;
  if (mode === 'embed') {
    const embedUrl = buildEmbedUrl(app.getCode());
    const iframe = `<iframe src="${embedUrl}" width="600" height="400" frameborder="0" sandbox="allow-scripts allow-same-origin" allow="fullscreen" style="border:1px solid #23272e;border-radius:6px"></iframe>`;
    navigator.clipboard.writeText(iframe).then(() => showToast('Embed code copied!'))
      .catch(() => prompt('Copy this embed code:', iframe));
    window.umami?.track('share_link', { mode: 'embed' });
    return;
  }
  url = mode === 'preview' ? buildPreviewUrl(app.getCode()) : buildShareUrl(app.getCode());
  if (url.length > 8000) {
    alert(`Link is ${url.length.toLocaleString()} chars — may be too long for some browsers. Consider saving as .scad instead.`);
  }
  const toastMsg = mode === 'preview' ? 'Preview link copied!' : 'Share link copied!';
  navigator.clipboard.writeText(url).then(() => showToast(toastMsg))
    .catch(() => prompt('Copy this link:', url));
  window.umami?.track('share_link', { mode });
}

document.getElementById('menu-share')!.addEventListener('click', () => doShare('link'));
document.getElementById('menu-share-preview')!.addEventListener('click', () => doShare('preview'));
document.getElementById('menu-share-embed')!.addEventListener('click', () => doShare('embed'));

const tbShareBtn = document.getElementById('tb-share-btn');
if (tbShareBtn) tbShareBtn.addEventListener('click', () => doShare('link'));

// --- View menu items ---

document.getElementById('menu-view-fit')!.addEventListener('click', () => viewport.fitToSelection());
document.getElementById('menu-view-reset')!.addEventListener('click', () => app.resetCamera());
document.getElementById('menu-view-ortho')!.addEventListener('click', () => syncProjectionUI(viewport.toggleProjection()));
document.getElementById('menu-view-wireframe')!.addEventListener('click', () => {
  const isWire = app.toggleWireframe();
  wireframeBtn.classList.toggle('active', isWire);
  if (solidBtn) solidBtn.classList.toggle('active', !isWire);
});
document.getElementById('menu-view-console')!.addEventListener('click', toggleConsolePanel);

// --- Undo / Redo (placeholder — rely on CodeMirror's built-in) ---
document.getElementById('menu-undo')!.addEventListener('click', () => {
  document.execCommand('undo');
});
document.getElementById('menu-redo')!.addEventListener('click', () => {
  document.execCommand('redo');
});

// --- AI Assistant (docked column) ---

const aiPanelMount = document.getElementById('ai-panel-mount')!;
const aiPanel = new AIChatPanel(aiPanelMount, app);
aiPanelRef = aiPanel;
const aiSettingsModal = createSettingsModal();

// Always-open AI panel (it's a docked column now)
if (!aiPanel.isOpen()) aiPanel.open();

// Header gear → open settings
aiPanelMount.addEventListener('ai-open-settings', () => aiSettingsModal.open());

document.getElementById('menu-ai-settings')!.addEventListener('click', () => aiSettingsModal.open());

aiSettingsModal.onSave(() => {
  aiPanel.refreshProvider();
});

// --- AI panel visibility toggle ---

const AI_HIDDEN_KEY = 'scadplus-ai-hidden';
const aiToggleBtn = document.getElementById('ai-toggle-btn') as HTMLButtonElement | null;
function applyAIHidden(hidden: boolean): void {
  document.body.classList.toggle('ai-hidden', hidden);
  if (aiToggleBtn) aiToggleBtn.classList.toggle('active', !hidden);
  localStorage.setItem(AI_HIDDEN_KEY, hidden ? '1' : '0');
}
applyAIHidden(localStorage.getItem(AI_HIDDEN_KEY) === '1');
if (aiToggleBtn) {
  aiToggleBtn.addEventListener('click', () => {
    applyAIHidden(!document.body.classList.contains('ai-hidden'));
  });
}

// --- Column splitter drag ---

const mainEl = document.querySelector('.main') as HTMLElement;
const COL_STORAGE = 'scadplus-col-widths';

interface ColWidths { editor?: number; ai?: number; drawer?: number; }
function loadCols(): ColWidths {
  try { return JSON.parse(localStorage.getItem(COL_STORAGE) || '{}'); } catch { return {}; }
}
function saveCols(w: ColWidths): void {
  localStorage.setItem(COL_STORAGE, JSON.stringify(w));
}
const cols = loadCols();
// Clamp stored widths so a stale oversized value can't squeeze other columns
const mainW = mainEl.getBoundingClientRect().width;
const aiDefault = Math.min(cols.ai || 320, 500);
const maxEditor = mainW - 44 - 240 - 4 - 4 - aiDefault - 200; // reserve ≥200px for viewport
if (cols.editor && cols.editor > 0 && cols.editor < maxEditor) {
  mainEl.style.setProperty('--w-editor', cols.editor + 'px');
} else {
  cols.editor = undefined;
}
if (cols.ai) mainEl.style.setProperty('--w-ai', aiDefault + 'px');
if (cols.drawer && cols.drawer >= 180 && cols.drawer <= 480) {
  const sty = document.createElement('style');
  sty.id = 'drawer-width-override';
  sty.textContent = `body.drawer-open .main { --w-drawer: ${cols.drawer}px; }`;
  document.head.appendChild(sty);
}

// Track indices in grid-template-columns:
//   0: 44px (rail)
//   1: drawer
//   2: drawer splitter
//   3: editor
//   4: editor-vp splitter
//   5: viewport
//   6: vp-ai splitter
//   7: ai
const TRACK_IDX: Record<'drawer' | 'editor' | 'ai', number> = { drawer: 1, editor: 3, ai: 7 };

function setupSplitter(id: string, target: 'editor' | 'ai' | 'drawer'): void {
  const el = document.getElementById(id);
  if (!el) return;
  let startX = 0, startW = 0;
  let dragging = false;

  function currentPx(): number {
    const cs = getComputedStyle(mainEl);
    const tpl = cs.gridTemplateColumns.split(' ');
    return parseFloat(tpl[TRACK_IDX[target]]);
  }

  function applyDrawer(w: number): void {
    // Drawer width is driven by a CSS rule tied to body.drawer-open — we
    // override it via a per-session <style> tag.
    let sty = document.getElementById('drawer-width-override') as HTMLStyleElement | null;
    if (!sty) {
      sty = document.createElement('style');
      sty.id = 'drawer-width-override';
      document.head.appendChild(sty);
    }
    sty.textContent = `body.drawer-open .main { --w-drawer: ${w}px; }`;
  }

  el.addEventListener('pointerdown', (e) => {
    e.preventDefault();
    dragging = true;
    el.classList.add('dragging');
    startX = e.clientX;
    startW = currentPx();
    document.body.style.cursor = 'col-resize';
    document.body.style.userSelect = 'none';
    (el as HTMLElement).setPointerCapture(e.pointerId);
  });
  el.addEventListener('pointermove', (e) => {
    if (!dragging) return;
    const dx = e.clientX - startX;
    let newW: number;
    if (target === 'editor') {
      newW = Math.max(240, startW + dx);
      const totalW = mainEl.getBoundingClientRect().width - 44 - 4 - 4;
      const aiW = cols.ai || 320;
      newW = Math.min(newW, totalW - aiW - 200);
      mainEl.style.setProperty('--w-editor', newW + 'px');
      cols.editor = newW;
    } else if (target === 'ai') {
      newW = Math.max(240, Math.min(640, startW - dx));
      mainEl.style.setProperty('--w-ai', newW + 'px');
      cols.ai = newW;
    } else if (target === 'drawer') {
      newW = Math.max(180, Math.min(480, startW + dx));
      applyDrawer(newW);
      cols.drawer = newW;
    }
  });
  function end(e: PointerEvent): void {
    if (!dragging) return;
    dragging = false;
    el!.classList.remove('dragging');
    document.body.style.cursor = '';
    document.body.style.userSelect = '';
    try { (el as HTMLElement).releasePointerCapture(e.pointerId); } catch {}
    saveCols(cols);
  }
  el.addEventListener('pointerup', end);
  el.addEventListener('pointercancel', end);
}
setupSplitter('split-drawer-editor', 'drawer');
setupSplitter('split-editor-vp', 'editor');
setupSplitter('split-vp-ai', 'ai');

// --- 2D Sketch Editor integration ---
{
  // Lazy-load the sketch editor only when the user first clicks "New Sketch".
  let sketchEditor: import('../viewport/SketchEditor2D').SketchEditor2D | null = null;

  // Inline copy of findSketchAtCursor so updateEditButton can run
  // synchronously on every cursor event. Importing the real one from
  // SketchEditor2D.ts would pull in the whole ~3000-line editor module
  // eagerly, defeating the click-to-lazy-load for that panel.
  const findSketchAtCursorLocal = (source: string, cursor: number): string | null => {
    const re = /\b([a-zA-Z_][a-zA-Z0-9_]*)\s*=\s*sketch\s*\(/g;
    let m: RegExpExecArray | null;
    while ((m = re.exec(source))) {
      const start = m.index;
      const openParen = re.lastIndex - 1;
      // Inline bracket matcher — handles strings + comments.
      let depth = 0;
      let closeParen = -1;
      let inStr: false | '"' | "'" = false;
      for (let i = openParen; i < source.length; i++) {
        const c = source[i];
        if (inStr) {
          if (c === '\\') { i++; continue; }
          if (c === inStr) inStr = false;
          continue;
        }
        if (c === '"' || c === "'") { inStr = c; continue; }
        if (c === '/' && source[i + 1] === '/') { while (i < source.length && source[i] !== '\n') i++; continue; }
        if (c === '/' && source[i + 1] === '*') { i += 2; while (i < source.length - 1 && !(source[i] === '*' && source[i + 1] === '/')) i++; i++; continue; }
        if (c === '(') depth++;
        else if (c === ')') { depth--; if (depth === 0) { closeParen = i; break; } }
      }
      if (closeParen < 0) continue;
      if (cursor >= start && cursor <= closeParen + 1) return m[1];
    }
    return null;
  };

  const btnNew = document.getElementById('sketch-new') as HTMLButtonElement | null;
  const btnEdit = document.getElementById('sketch-edit') as HTMLButtonElement | null;
  const pop = document.getElementById('planePop') as HTMLElement | null;
  const popClose = document.getElementById('planePopClose') as HTMLElement | null;
  const planeBtns = document.querySelectorAll<HTMLButtonElement>('.plane-btn');

  let cursorSketchVar: string | null = null;

  const ensureEditor = async (): Promise<import('../viewport/SketchEditor2D').SketchEditor2D> => {
    if (!sketchEditor) {
      const mod = await import('../viewport/SketchEditor2D');
      sketchEditor = new mod.SketchEditor2D();
    }
    return sketchEditor;
  };

  const closePlanePop = (): void => { if (pop) pop.setAttribute('hidden', ''); };
  const openPlanePop = (): void => {
    if (!pop || !btnNew) return;
    const r = btnNew.getBoundingClientRect();
    pop.style.top = `${Math.round(r.bottom + 6)}px`;
    pop.style.left = `${Math.round(Math.max(8, r.right - 200))}px`;
    pop.removeAttribute('hidden');
  };

  // Insert a `sketchN = sketch(plane=..., origin=..., entities=[...])`
  // assignment at the head of source and return the chosen variable name.
  // `entitiesBody` is optional pre-formatted entity lines (each line is one
  // entity, comma-terminated, leading 2-space indent). When omitted the
  // scaffold has an empty entity array.
  const insertSketchScaffold = (plane: string, origin: [number, number, number], entitiesBody = ''): string => {
    const src = app.getCode();
    let n = 1;
    const rx = /\bsketch(\d+)\s*=/g;
    let m: RegExpExecArray | null;
    while ((m = rx.exec(src))) n = Math.max(n, parseInt(m[1], 10) + 1);
    const varName = `sketch${n}`;
    const originStr = `[${origin[0]}, ${origin[1]}, ${origin[2]}]`;
    const planeStr = /^[A-Z]{2,3}$/.test(plane) ? `"${plane}"` : plane;
    const snippet =
      `${varName} = sketch(plane=${planeStr}, origin=${originStr}, entities=[\n${entitiesBody}]);\n` +
      `// preview: linear_extrude(5) polygon(${varName});\n\n`;
    const newCode = snippet + src;
    app.setCode(newCode);
    return varName;
  };

  // Rotation matrix that takes [0,0,1] onto a unit normal vector. Same math
  // as `rotationFromTo` in the evaluator — duplicated here so the bridge can
  // project face-outline edges into sketch-local 2D coords using the exact
  // same basis the sketch editor / extruder uses for arbitrary planes.
  const rotationFromZTo = (to: [number, number, number]): number[] => {
    const dx = to[0]; const dy = to[1]; const dz = to[2];
    const m = Math.hypot(dx, dy, dz) || 1;
    const nx = dx / m; const ny = dy / m; const nz = dz / m;
    const d = nz; // [0,0,1]·n
    if (d > 1 - 1e-8) return [1, 0, 0, 0, 1, 0, 0, 0, 1];
    if (d < -1 + 1e-8) {
      // 180° about X axis
      return [1, 0, 0, 0, -1, 0, 0, 0, -1];
    }
    // axis = [0,0,1] × n = [-ny, nx, 0], normalized
    const ax = -ny, ay = nx, az = 0;
    const al = Math.hypot(ax, ay, az) || 1;
    const ux = ax / al, uy = ay / al, uz = az / al;
    const angle = Math.acos(Math.max(-1, Math.min(1, d)));
    const c = Math.cos(angle); const s = Math.sin(angle); const t = 1 - c;
    return [
      t*ux*ux + c,    t*ux*uy - s*uz, t*ux*uz + s*uy,
      t*ux*uy + s*uz, t*uy*uy + c,    t*uy*uz - s*ux,
      t*ux*uz - s*uy, t*uy*uz + s*ux, t*uz*uz + c,
    ];
  };

  const openEditorFor = async (varName: string): Promise<void> => {
    const ed = await ensureEditor();
    const host: import('../viewport/SketchEditor2D').EditorHost = {
      getSource: () => app.getCode(),
      replaceRange: (from: number, to: number, text: string) => {
        // Incremental dispatch — preserves undo history. `app.setCode`
        // would wipe history (that path is reserved for full-document
        // loads like tab switches).
        app.replaceRange(from, to, text);
      },
      undo: () => { app.undo(); },
      redo: () => { app.redo(); },
      viewPlane: (plane, origin, upScad) => {
        app.getViewport().viewSketchPlane(plane, origin, upScad);
      },
      panPlaneView: (dx, dy) => { app.getViewport().panSketchView(dx, dy); },
      setPlaneOrthoSize: (w, h) => { app.getViewport().setSketchOrthoSize(w, h); },
      zoomPlaneView: (x, y, factor) => { app.getViewport().zoomSketchView(x, y, factor); },
      setGridVisible: (v) => { app.getViewport().setGridVisible(v); },
      setChromeVisible: (v) => { app.getViewport().setViewportChromeVisible(v); },
      setModelOpacity: (o) => { app.getViewport().setModelOpacity(o); },
      clearSelection: () => { app.getViewport().deselectAll(); },
      getPlaneViewExtent: () => app.getViewport().getSketchViewExtent(),
      saveView: () => { app.getViewport().saveSketchViewState(); },
      restoreView: () => { app.getViewport().restoreSketchViewState(); },
      captureView: () => app.getViewport().captureViewSnapshot(),
      applyView: (snap) => { app.getViewport().applyViewSnapshot(snap as import('../viewport/ViewportManager').ViewSnapshot); },
      onClose: () => {
        // No special teardown; run the pipeline to refresh 3D
      },
    };
    ed.open(host, varName);
  };

  btnNew?.addEventListener('click', (e) => {
    e.stopPropagation();
    if (!pop) return;
    if (pop.hasAttribute('hidden')) openPlanePop();
    else closePlanePop();
  });
  popClose?.addEventListener('click', closePlanePop);
  document.addEventListener('mousedown', (e) => {
    if (!pop || pop.hasAttribute('hidden')) return;
    const t = e.target as Node;
    if (pop.contains(t) || btnNew?.contains(t)) return;
    closePlanePop();
  });

  // Track cursor position → toggle the "Edit Sketch" button when cursor is
  // inside a `sketchN = sketch(...)` assignment. Runs synchronously so
  // cursorSketchVar is always up-to-date before the user's next click — an
  // async version would leave a one-event lag, loading the previous sketch.
  const updateEditButton = (offset: number): void => {
    const name = findSketchAtCursorLocal(app.getCode(), offset);
    cursorSketchVar = name;
    if (btnEdit) {
      btnEdit.disabled = !name;
      btnEdit.classList.toggle('active', !!name);
      btnEdit.textContent = name ? `Edit ${name}` : 'Edit Sketch';
      btnEdit.title = name ? `Open 2D editor for ${name}` : 'Place caret inside a sketch(...) block to enable';
    }
    // If the 2D editor is already open and the cursor moved into a
    // DIFFERENT sketch assignment, switch the editor to that sketch.
    if (name && sketchEditor && sketchEditor.isOpen()) sketchEditor.switchTo(name);
  };
  app.onCursorChange((offset) => { updateEditButton(offset); });

  btnEdit?.addEventListener('click', async () => {
    if (!cursorSketchVar) return;
    await openEditorFor(cursorSketchVar);
  });

  const facePanel = document.getElementById('planePopFace') as HTMLElement | null;
  const faceInfo = document.getElementById('planePopFaceInfo') as HTMLElement | null;
  let awaitingFaceRightClick = false;
  // 'plane'        → empty sketch on the picked face's plane, origin = click pt
  // 'plane-center' → empty sketch, origin anchored at the face's centroid
  // 'outline'      → also copy the face's boundary edges into the sketch
  // 'ref'          → copy edges as construction (reference) lines
  let faceMode: 'plane' | 'plane-center' | 'outline' | 'ref' = 'plane';

  const fromFaceBtn = document.querySelector<HTMLButtonElement>('.plane-btn[data-plane="from-face"]');
  const fromFaceCenterBtn = document.querySelector<HTMLButtonElement>('.plane-btn[data-plane="from-face-center"]');
  const copyFaceBtn = document.querySelector<HTMLButtonElement>('.plane-btn[data-plane="copy-face"]');
  const copyFaceRefBtn = document.querySelector<HTMLButtonElement>('.plane-btn[data-plane="copy-face-ref"]');

  const resetFacePanel = (): void => {
    awaitingFaceRightClick = false;
    faceMode = 'plane';
    app.getViewport().setFaceHoverMode(false);
    fromFaceBtn?.classList.remove('active');
    fromFaceCenterBtn?.classList.remove('active');
    copyFaceBtn?.classList.remove('active');
    copyFaceRefBtn?.classList.remove('active');
    if (facePanel) facePanel.setAttribute('hidden', '');
    if (faceInfo) {
      faceInfo.textContent = 'No face selected yet — right-click a face in the 3D view.';
      faceInfo.classList.remove('has-face');
    }
  };

  planeBtns.forEach((b) => {
    b.addEventListener('click', async () => {
      const plane = b.dataset.plane || 'XY';
      if (plane === 'from-face' || plane === 'from-face-center' || plane === 'copy-face' || plane === 'copy-face-ref') {
        // Enter face-pick mode. Right-click on a 3D face will create the
        // sketch directly and open the 2D editor — no extra confirm step.
        // 'from-face-center' anchors origin at face centroid; the copy-* modes
        // additionally seed the new sketch with the face's boundary edges.
        awaitingFaceRightClick = true;
        faceMode =
          plane === 'from-face-center' ? 'plane-center'
          : plane === 'copy-face'      ? 'outline'
          : plane === 'copy-face-ref'  ? 'ref'
          : 'plane';
        app.getViewport().setFaceHoverMode(true);
        fromFaceBtn?.classList.toggle('active', faceMode === 'plane');
        fromFaceCenterBtn?.classList.toggle('active', faceMode === 'plane-center');
        copyFaceBtn?.classList.toggle('active', faceMode === 'outline');
        copyFaceRefBtn?.classList.toggle('active', faceMode === 'ref');
        if (facePanel) facePanel.removeAttribute('hidden');
        if (faceInfo) {
          faceInfo.textContent =
            faceMode === 'outline'      ? 'Hover to preview, right-click a face to create a sketch with its outline.'
            : faceMode === 'ref'        ? 'Hover to preview, right-click a face to create a sketch with its outline as reference lines.'
            : faceMode === 'plane-center' ? 'Hover to preview, right-click a face — origin will be anchored at the face centroid.'
            : 'Hover to preview, right-click a face to create the sketch.';
          faceInfo.classList.remove('has-face');
        }
        return;
      }
      closePlanePop();
      resetFacePanel();
      const varName = insertSketchScaffold(plane, [0, 0, 0]);
      await openEditorFor(varName);
    });
  });

  // Right-click-in-3D only creates a sketch when the user has explicitly
  // requested "From Face…". Otherwise the browser's default context menu is
  // suppressed but nothing else happens. On pick, the sketch is created
  // immediately and the 2D editor opens — no extra "Create" step.
  (window as any).__scadplusOpenSketchOnFace = async (
    normal: [number, number, number],
    point: [number, number, number],
    edges?: [number, number, number][][],
    center?: [number, number, number] | null,
  ) => {
    if (!awaitingFaceRightClick) return;
    // Capture the mode before resetFacePanel() clears it back to 'plane'.
    const mode = faceMode;
    // Origin: face centroid for 'plane-center', else the click point.
    const src = mode === 'plane-center' && center ? center : point;
    const origin: [number, number, number] = [
      +src[0].toFixed(4), +src[1].toFixed(4), +src[2].toFixed(4),
    ];
    resetFacePanel();
    closePlanePop();
    const planeStr = `[${normal[0].toFixed(4)}, ${normal[1].toFixed(4)}, ${normal[2].toFixed(4)}]`;

    // Project the picked face's boundary edges into sketch-local 2D.
    // Frame: R rotates [0,0,1] onto `normal`. Local U / V basis vectors are
    // the first two columns of R. Local coord = R^T·(P - origin), where
    // row 0 of R^T = column 0 of R = [R[0], R[3], R[6]].
    // 'plane' = bare sketch, no outline. The other three modes seed the
    // sketch with the face's boundary edges; 'ref' marks them construction.
    let entitiesBody = '';
    if ((mode === 'outline' || mode === 'ref' || mode === 'plane-center') && edges && edges.length > 0) {
      const R = rotationFromZTo(normal);
      const uX = R[0], uY = R[3], uZ = R[6];
      const vX = R[1], vY = R[4], vZ = R[7];
      const project = (p: [number, number, number]): [number, number] => {
        const dx = p[0] - origin[0];
        const dy = p[1] - origin[1];
        const dz = p[2] - origin[2];
        return [
          +(dx * uX + dy * uY + dz * uZ).toFixed(2),
          +(dx * vX + dy * vY + dz * vZ).toFixed(2),
        ];
      };
      const constructionSuffix = mode === 'ref' ? ', construction=true' : '';
      const lines: string[] = [];
      for (const [a, b] of edges) {
        const ua = project(a);
        const ub = project(b);
        if (Math.hypot(ua[0] - ub[0], ua[1] - ub[1]) < 0.001) continue;
        lines.push(`  line(from=[${ua[0]}, ${ua[1]}], to=[${ub[0]}, ${ub[1]}]${constructionSuffix}),`);
      }
      if (lines.length > 0) entitiesBody = lines.join('\n') + '\n';
    }

    const varName = insertSketchScaffold(planeStr, origin, entitiesBody);
    await openEditorFor(varName);
  };

  // Hide the face sub-panel when the popover closes so its stale info
  // doesn't leak into the next New Sketch.
  popClose?.addEventListener('click', resetFacePanel);

  // Esc while waiting for a face pick → cancel the mode. Otherwise the
  // crosshair cursor stays armed even after the user changes their mind.
  document.addEventListener('keydown', (e) => {
    if (e.key === 'Escape' && awaitingFaceRightClick) {
      e.stopPropagation();
      resetFacePanel();
    }
  }, true);
}

} // end if (!isPreview)

// --- Init WASM ---

app.initWasm().then(() => {
  // WASM ready
}).catch((err) => {
  console.error('Failed to initialize Manifold:', err);
});
